import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import api from './api/axios';
import { toast, Toaster } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card.jsx';
import { Button } from './components/ui/button.jsx';
import { Input } from './components/ui/input.jsx';
import { Label } from './components/ui/label.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select.jsx';
import { Textarea } from './components/ui/textarea.jsx';
import { Badge } from './components/ui/badge.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/simple-tabs.jsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog.jsx';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './components/ui/alert-dialog.jsx';
import { Avatar, AvatarFallback } from './components/ui/avatar.jsx';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from './components/ui/sheet.jsx';
import { Checkbox } from './components/ui/checkbox.jsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table.jsx';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from './components/ui/dropdown-menu.jsx';
const ExpensesTab = lazy(() => import('./components/ExpensesTab').then((m) => ({ default: m.ExpensesTab })));
const PhotoViewerDialog = lazy(() => import('./components/PhotoViewerDialog').then((m) => ({ default: m.PhotoViewerDialog })));
const BusinessTab = lazy(() => import('./components/BusinessTab').then((m) => ({ default: m.BusinessTab })));
import {
  DollarSign,
  Users,
  MapPin,
  Plus,
  LogOut,
  User,
  Settings,
  BarChart3,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Download,
  Eye,
  EyeOff,
  Lock,
  Shield,
  Activity,
  Calendar,
  TrendingUp,
  Receipt,
  Edit,
  Trash2,
  Camera,
  PhoneCall,
  CreditCard,
  AlertTriangle,
  RotateCcw,
  Archive,
  Key,
  UserCheck,
  MapPinned,
  RefreshCw,
  Zap,
  Building2,
  Phone,
  Mail,
  Save,
  Filter,
  Search,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Cpu,
  Star,
  Heart,
  Globe,
  Home,
  MapIcon,
  Building,
  UserPlus,
  Target,
  ArrowUp,
  ArrowDown,
  TrendingDown,
  Info,
  AlertCircle,
  PlusCircle,
  MinusCircle,
  Calculator,
  Check,
  X,
  Clock4,
  History,
  FileImage,
  Loader,
  MoreHorizontal,
  Copy,
  Upload,
  Archive as ArchiveIcon,
  Ban,
  Undo
} from 'lucide-react';
import './App.css';

const NETWORK_PERCENT_OPTIONS = Array.from({ length: 21 }, (_, index) => `${index * 5}%`);
const BANDWIDTH_OPTIONS = ['airtel', 'vi', 'jio', 'bsnl', 'others'];
const PPA_OPTIONS = ['yes', 'no'];

const getBandwidthSelectValue = (bandwidth) => {
  if (!bandwidth) return '';
  if (BANDWIDTH_OPTIONS.includes(bandwidth)) return bandwidth;
  return 'others';
};

const normalizePercentValue = (rawValue) => {
  const cleaned = String(rawValue ?? '').trim();
  if (!cleaned) return '';
  const withoutPercent = cleaned.replace('%', '').trim();
  const numeric = withoutPercent.replace(/[^0-9]/g, '');
  if (!numeric) return '';
  let value = parseInt(numeric, 10);
  if (Number.isNaN(value)) return '';
  if (value < 0) value = 0;
  if (value > 100) value = 100;
  return `${value}%`;
};

const getLocationDisplay = (visit) => {
  const name =
    visit?.location?.resolved_name ||
    visit?.location?.location_name ||
    visit?.location_name ||
    visit?.visit_purpose ||
    '';
  const lat = visit?.location?.latitude;
  const lng = visit?.location?.longitude;
  const mapsUrl =
    visit?.location?.google_maps_url ||
    visit?.location?.maps_url ||
    (lat !== undefined && lng !== undefined ? `https://www.google.com/maps?q=${lat},${lng}` : null);
  const maskedCoords =
    lat !== undefined && lng !== undefined ? `Approx. ${Number(lat).toFixed(3)}, ${Number(lng).toFixed(3)}` : '';
  return {
    label: name || maskedCoords || 'Location unavailable',
    mapsUrl,
  };
};

const isWithinLast24Hours = (timestamp) => {
  if (!timestamp) return false;
  const created = new Date(timestamp);
  if (Number.isNaN(created.getTime())) return false;
  return Date.now() - created.getTime() <= 24 * 60 * 60 * 1000;
};

// Format date to YYYY-MM-DD for API submission
const formatDateForAPI = (dateInput) => {
  if (!dateInput) return new Date().toISOString().split('T')[0];

  // If already in YYYY-MM-DD format, return as is
  if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    return dateInput;
  }

  // Parse and format the date
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) {
    // Invalid date, return today
    return new Date().toISOString().split('T')[0];
  }

  return date.toISOString().split('T')[0];
};

// Shared Allen Group logo component (used in header and login)
function AllenGroupLogo({ size = 'md' }) {
  // Size mappings for the logo image
  const sizeMap = {
    'sm': 'h-8',      // Small: 32px height
    'md': 'h-10',     // Medium: 40px height
    'lg': 'h-12'      // Large: 48px height
  };

  const heightClass = sizeMap[size] || sizeMap['md'];

  return (
    <img
      src="/allen-group-logo.jpg"
      alt="Allen Group"
      className={`${heightClass} w-auto object-contain`}
    />
  );
}

// Auth Context
const AuthContext = React.createContext();
const RefreshContext = React.createContext();

// Main App Component
function App() {

  // Safe error message extraction for toast notifications
  const getErrorMessage = (error, fallback = 'Something went wrong') => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      // FastAPI 422 validation errors come as array of objects
      return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
    }
    if (detail && typeof detail === 'object') return JSON.stringify(detail);
    return error?.response?.data?.message || error?.message || fallback;
  };

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');

    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await api.get(`/api/auth/me`);

      if (response.data && response.data.id) {
        setUser(response.data);
        if (response.data.must_change_password) {
          setForcePasswordChange(true);
        }
      } else {
        throw new Error('Invalid user data received');
      }
    } catch (error) {
      // Clear invalid session
      localStorage.removeItem('token');
      localStorage.removeItem('rememberedUsername');
      delete api.defaults.headers.common['Authorization'];
      setUser(null);

      // Only show error if it's not a simple token absence
      if (localStorage.getItem('token')) {
        toast.error('Session expired. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      // Clear any existing tokens first
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];

      if (process.env.NODE_ENV !== 'production') {
        console.log('Sending login request:', credentials);
      }
      const response = await api.post(`/api/auth/login`, credentials);
      if (process.env.NODE_ENV !== 'production') {
        console.log('Login response:', response.data);
      }

      if (!response.data || !response.data.access_token) {
        throw new Error('Invalid response from server');
      }

      const { access_token, user: userData } = response.data;

      // Set token and auth header
      localStorage.setItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Verify token works by fetching user data
      const verifyResponse = await api.get(`/api/auth/me`);
      if (verifyResponse.data) {
        setUser(verifyResponse.data);
        // Check if user must change password on first login
        if (verifyResponse.data.must_change_password) {
          setForcePasswordChange(true);
        }
      } else {
        throw new Error('Failed to verify user session');
      }

      // Save username only if remember me is checked (never store passwords in localStorage)
      if (credentials.remember_me) {
        localStorage.setItem('rememberedUsername', credentials.username);
      } else {
        localStorage.removeItem('rememberedUsername');
      }

      if (userData.must_change_password) {
        toast.info('Please change your password to continue.');
      } else {
        toast.success(`Welcome back, ${userData.full_name}!`);
      }
      return true;
    } catch (error) {
      // Clear everything on error
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      setUser(null);

      if (error.code === 'NETWORK_ERROR' || error.message?.includes('fetch')) {
        toast.error('Network error: Please check your connection and try again');
      } else if (error.response?.status === 401) {
        toast.error('Invalid username or password');
      } else if (error.response?.status >= 500) {
        toast.error('Server error: Please try again later');
      } else {
        toast.error(getErrorMessage(error, 'Login failed'));
      }
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('rememberedUsername');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-red-50">
        <div className="relative mb-8">
          <div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-200 border-t-blue-600"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Zap className="w-8 h-8 text-blue-600 animate-pulse" />
          </div>
        </div>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">ALLENTEK INDIA</h2>
          <p className="text-gray-600 font-medium animate-pulse">Loading System...</p>
        </div>
      </div>
    );
  }

  const refreshAllData = () => {
    // This function can be called to refresh all data across components
    window.dispatchEvent(new CustomEvent('refreshData'));
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, setUser, forcePasswordChange, setForcePasswordChange }}>
      <RefreshContext.Provider value={{ refreshAllData }}>
        <Router>
          <div className="min-h-screen">
            <Toaster
              position="top-right"
              closeButton
              toastOptions={{
                className: 'text-sm py-2 px-3 min-w-[220px] max-w-[320px]'
              }}
            />
            {forcePasswordChange && user && <ForcePasswordChangeModal />}
            <Routes>
              <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/dashboard" />} />
              <Route path="/dashboard" element={user ? (forcePasswordChange ? <Navigate to="/dashboard" /> : <Dashboard />) : <Navigate to="/login" />} />
              <Route path="/users/:userId" element={user ? (forcePasswordChange ? <Navigate to="/dashboard" /> : <UserProfilePage />) : <Navigate to="/login" />} />
              <Route path="/" element={<Navigate to={user ? "/dashboard" : "/login"} />} />
            </Routes>
          </div>
        </Router>
      </RefreshContext.Provider>
    </AuthContext.Provider>
  );
}


// Force Password Change Modal - shown on first login
function ForcePasswordChangeModal() {
  const { user, setUser, setForcePasswordChange } = React.useContext(AuthContext);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleForcePasswordChange = async () => {
    if (!passwordData.current_password || !passwordData.new_password) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (passwordData.current_password === passwordData.new_password) {
      toast.error('New password must be different from current password');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/api/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully! You can now access the system.');
      setForcePasswordChange(false);
      // Refresh user data to clear the must_change_password flag
      const refreshed = await api.get('/api/auth/me');
      if (refreshed.data) {
        setUser(refreshed.data);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to change password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Lock className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Change Your Password</h2>
              <p className="text-blue-100 text-sm mt-1">For security, please set a new password before continuing</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              This is your first login. You must change your password to access the system.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Current Password</Label>
            <div className="relative">
              <Input
                type={showCurrentPassword ? 'text' : 'password'}
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                placeholder="Enter current password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">New Password</Label>
            <div className="relative">
              <Input
                type={showNewPassword ? 'text' : 'password'}
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                placeholder="Enter new password (min 6 characters)"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Confirm New Password</Label>
            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                placeholder="Confirm new password"
                className="pr-10"
                onKeyDown={(e) => e.key === 'Enter' && handleForcePasswordChange()}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            onClick={handleForcePasswordChange}
            disabled={isSubmitting}
            className="w-full bg-blue-600 hover:bg-blue-700 h-11 font-medium"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Changing Password...
              </div>
            ) : (
              'Change Password & Continue'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Enhanced Login Page with Show/Hide Password
function LoginPage() {
  const [credentials, setCredentials] = useState({ username: '', password: '', remember_me: false });
  const [isLoading, setIsLoading] = useState(false);
  const [showAnimation, setShowAnimation] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = React.useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    // Load remembered username only (never store passwords)
    const rememberedUsername = localStorage.getItem('rememberedUsername');
    if (rememberedUsername) {
      setCredentials(prev => ({
        ...prev,
        username: rememberedUsername,
        remember_me: true
      }));
    }

    // Hide animation after a short delay
    const timer = setTimeout(() => setShowAnimation(false), 600);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!credentials.username || !credentials.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    const success = await login(credentials);
    if (success) {
      navigate('/dashboard');
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#071226] via-[#0b1a33] to-[#071226]" />
      <div className="absolute inset-0 opacity-60 bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.35),transparent_45%),radial-gradient(circle_at_80%_30%,rgba(99,102,241,0.25),transparent_50%),radial-gradient(circle_at_50%_90%,rgba(14,165,233,0.18),transparent_55%)]" />

      <div className="absolute inset-0">
        {[...Array(28)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-white/20 animate-twinkle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-10">
        <div className={`w-full transform transition-all duration-700 ${showAnimation ? 'scale-[0.98] opacity-0' : 'scale-100 opacity-100'}`}>
          <Card className="mx-auto w-full max-w-lg backdrop-blur-2xl bg-white/5 border border-white/10 shadow-2xl rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />

            <CardHeader className="text-center pb-8 relative z-10 pt-10">
              <div className="mx-auto w-20 h-20 rounded-3xl bg-white/10 backdrop-blur-xl border border-white/15 flex items-center justify-center shadow-xl">
                <div className="w-16 h-16 rounded-2xl bg-slate-900/90 flex items-center justify-center shadow-lg border border-white/15">
                  <AllenGroupLogo size="sm" />
                </div>
              </div>

              <div className="mt-7 space-y-3">
                <h1 className="text-4xl font-black tracking-tight text-white">
                  <span>ALLENTEK</span>{' '}
                  <span className="text-blue-400">INDIA</span>
                </h1>
                <p className="text-white/60 text-xs font-semibold tracking-[0.35em]">
                  BUSINESS MANAGEMENT SYSTEM
                </p>
              </div>
            </CardHeader>

            <CardContent className="relative z-10 pb-10">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="username" className="text-white/75 font-semibold text-sm tracking-wide">USERNAME</Label>
                    <div className="relative mt-2">
                      <User className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-white/45" />
                      <Input
                        id="username"
                        type="text"
                        placeholder="Enter your username"
                        value={credentials.username}
                        onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                        className="h-14 pl-12 bg-white/5 backdrop-blur-sm border-white/10 text-white placeholder:text-white/30 rounded-xl text-base font-medium focus:bg-white/10 focus:border-white/20 transition-all duration-300"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="password" className="text-white/75 font-semibold text-sm tracking-wide">PASSWORD</Label>
                    <div className="relative mt-2">
                      <Lock className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-white/45" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={credentials.password}
                        onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                        className="h-14 pl-12 pr-12 bg-white/5 backdrop-blur-sm border-white/10 text-white placeholder:text-white/30 rounded-xl text-base font-medium focus:bg-white/10 focus:border-white/20 transition-all duration-300"
                        disabled={isLoading}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-white/55 hover:text-white hover:bg-white/10"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pt-1">
                    <Checkbox
                      id="remember_me"
                      checked={credentials.remember_me}
                      onCheckedChange={(checked) => setCredentials({ ...credentials, remember_me: Boolean(checked) })}
                      className="border-white/30 data-[state=checked]:bg-blue-500 data-[state=checked]:text-white"
                    />
                    <Label htmlFor="remember_me" className="text-white/60 font-medium">
                      Remember my credentials
                    </Label>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-14 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-lg rounded-xl shadow-2xl border border-white/10 transition-all duration-300"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                      <span>Authenticating...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-3">
                      <span>Access Dashboard</span>
                      <ChevronRight className="w-5 h-5" />
                    </div>
                  )}
                </Button>
              </form>

              <div className="mt-8">
                <div className="p-4 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/15 border border-blue-400/20 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-blue-300" />
                    </div>
                    <div className="text-left">
                      <div className="text-white/85 font-semibold">ENTERPRISE SECURITY</div>
                      <div className="text-white/50 text-sm">
                        Contact your system administrator for access credentials
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Enhanced Dashboard
function Dashboard() {
  const { user, logout } = React.useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('overview');

  // Determine available tabs based on user role hierarchy
  const getAvailableTabs = () => {
    const baseTabs = [
      { id: 'overview', label: 'Overview', icon: BarChart3, color: 'text-blue-600' }
    ];

    if (user?.role === 'user') {
      return [
        ...baseTabs,
        { id: 'expenses', label: 'My Expenses', icon: Receipt, color: 'text-green-600' },
        // { id: 'business', label: 'Business Activities', icon: PhoneCall, color: 'text-purple-600' },
        { id: 'machine-details', label: 'Machine Details', icon: Activity, color: 'text-teal-600' },
        { id: 'locations', label: 'My Visits', icon: MapPin, color: 'text-orange-600' }
      ];
    }

    if (user?.role === 'moderator') {
      return [
        ...baseTabs,
        { id: 'expenses', label: 'Team Expenses', icon: Receipt, color: 'text-green-600' },
        // { id: 'business', label: 'Team Activities', icon: PhoneCall, color: 'text-purple-600' },
        { id: 'users', label: 'Team Management', icon: Users, color: 'text-indigo-600' },
        { id: 'machine-details', label: 'Machine Details', icon: Activity, color: 'text-teal-600' },
        { id: 'locations', label: 'Team Locations', icon: MapPin, color: 'text-orange-600' }
      ];
    }

    if (user?.role === 'admin') {
      return [
        ...baseTabs,
        { id: 'expenses', label: 'All Expenses', icon: Receipt, color: 'text-green-600' },
        // { id: 'business', label: 'Business Operations', icon: PhoneCall, color: 'text-purple-600' },
        { id: 'users', label: 'User Management', icon: Users, color: 'text-indigo-600' },
        { id: 'machine-details', label: 'Machine Details', icon: Activity, color: 'text-teal-600' },
        { id: 'locations', label: 'All Locations', icon: MapPin, color: 'text-orange-600' }
      ];
    }

    // supervisor_admin
    return [
      ...baseTabs,
      { id: 'expenses', label: 'All Expenses', icon: Receipt, color: 'text-green-600' },
      // { id: 'business', label: 'Business Operations', icon: PhoneCall, color: 'text-purple-600' },
      { id: 'users', label: 'Organization', icon: Users, color: 'text-indigo-600' },
      { id: 'machine-details', label: 'Machine Details', icon: Activity, color: 'text-teal-600' },
      { id: 'locations', label: 'All Locations', icon: MapPin, color: 'text-orange-600' }
      // Removed Audit tab as requested
    ];
  };

  const availableTabs = getAvailableTabs();

  // Get role display name
  const getRoleDisplayName = (role) => {
    const roleMap = {
      'supervisor_admin': 'Supervisor',
      'admin': 'Admin',
      'moderator': 'Moderator',
      'user': 'Staff'
    };
    return roleMap[role] || role;
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* Enterprise Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 h-16 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full">
            {/* Branding */}
            <div className="flex items-center gap-3">
              <AllenGroupLogo size="sm" />
              <div className="hidden md:flex flex-col">
                <h1 className="text-lg font-bold text-slate-900 leading-tight tracking-tight">ALLENTEK INDIA</h1>
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Business Management System</span>
              </div>
            </div>

            {/* Right Section: User & Actions */}
            <div className="flex items-center gap-4">
              <SettingsPanel />

              <div className="h-8 w-px bg-slate-200 mx-1 hidden sm:block"></div>

              <div className="flex items-center gap-3 pl-1">
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8 ring-1 ring-slate-200">
                    <AvatarFallback className="bg-blue-100 text-blue-700 text-xs font-bold">
                      {user?.full_name?.split(' ').map(n => n[0]).join('') || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden lg:block text-sm">
                    <p className="font-semibold text-slate-700 leading-none">{user?.full_name}</p>
                    <p className="text-xs text-slate-500 mt-1 capitalize">{getRoleDisplayName(user?.role)}</p>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={logout}
                  className="text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-full h-9 w-9 transition-colors"
                  title="Logout"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* Navigation Bar */}
          <div className="sticky top-20 z-40 -mx-4 sm:mx-0 overflow-x-auto scrollbar-hide">
            <div className="bg-white border-y sm:border border-slate-200 sm:rounded-xl shadow-sm px-2 py-2 min-w-max sm:min-w-0">
              <TabsList className="bg-transparent h-auto p-0 gap-1 flex justify-start sm:justify-center w-full">
                {availableTabs.map((tab) => (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className="flex-1 min-w-[100px] flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-slate-500 font-medium text-sm transition-all
                      data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:shadow-none
                      hover:bg-slate-50 hover:text-slate-700"
                  >
                    <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-500'}`} />
                    <span>{tab.label}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          </div>

          {/* Tab Content Wrappers */}
          <div className="min-h-[500px]">
            <TabsContent value="overview" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <OverviewTab />
            </TabsContent>

            <TabsContent value="expenses" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Suspense fallback={(
                <div className="flex flex-col items-center justify-center py-24 text-slate-400">
                  <Loader className="w-8 h-8 animate-spin mb-4 text-blue-500" />
                  <p className="text-sm">Loading Expenses...</p>
                </div>
              )}>
                <ExpensesTab currentUser={user} />
              </Suspense>
            </TabsContent>

            <TabsContent value="business" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Suspense fallback={(
                <div className="flex flex-col items-center justify-center py-24 text-slate-400">
                  <Loader className="w-8 h-8 animate-spin mb-4 text-purple-500" />
                  <p className="text-sm">Loading Business Data...</p>
                </div>
              )}>
                <BusinessTab />
              </Suspense>
            </TabsContent>

            {(user?.role !== 'user') && (
              <TabsContent value="users" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
                <UsersTab />
              </TabsContent>
            )}

            <TabsContent value="machine-details" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <MachineDetailsDefaultsTab />
            </TabsContent>

            <TabsContent value="locations" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <LocationsTab />
            </TabsContent>
          </div>
        </Tabs>
      </main>
    </div>
  );
}

// Enhanced Settings Panel with More Options
function SettingsPanel() {
  const { user, setUser } = React.useContext(AuthContext);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [profileData, setProfileData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  // Individual visibility state
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordChanged, setPasswordChanged] = useState(() => {
    return localStorage.getItem(`passwordChanged_${user?.id}`) === 'true';
  });

  const handlePasswordChange = async () => {
    if (!passwordData.current_password || !passwordData.new_password) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setIsChangingPassword(true);
    try {
      await api.post(`/api/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });

      toast.success('Password changed successfully!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setPasswordChanged(true);
      localStorage.setItem(`passwordChanged_${user?.id}`, 'true');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to change password'));
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleProfileUpdate = async () => {
    setIsUpdatingProfile(true);
    try {
      const response = await api.put(`/api/users/${user.id}`, {
        full_name: profileData.full_name,
        email: profileData.email,
      });

      if (response.status === 200) {
        // Fetch updated user data from server to ensure consistency
        const userResponse = await api.get(`/api/auth/me`);
        setUser(userResponse.data);

        // Update local profile data state
        setProfileData({
          full_name: userResponse.data.full_name || '',
          email: userResponse.data.email || ''
        });

        toast.success('Profile updated successfully! Changes are now visible.');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'supervisor_admin': 'Supervisor',
      'admin': 'Admin',
      'moderator': 'Moderator',
      'user': 'Staff'
    };
    return roleMap[role] || role;
  };

  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-full h-9 w-9 transition-all">
          <Settings className="w-5 h-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto bg-white p-0 gap-0 overflow-hidden rounded-2xl">
        <DialogHeader className="border-b border-slate-100 p-6 pb-4">
          <DialogTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Settings className="w-5 h-5 text-slate-500" />
            Account Settings
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Manage your personal profile and security preferences.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
          {/* Profile Section */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
              <User className="w-4 h-4 text-blue-600" />
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Profile Information</h3>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="profile-name" className="text-xs font-semibold text-slate-500 mb-1.5 block">Full Name</Label>
                <Input
                  id="profile-name"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                  className="h-10 border-slate-200 focus:border-blue-500"
                />
              </div>
              <div>
                <Label htmlFor="profile-email" className="text-xs font-semibold text-slate-500 mb-1.5 block">Email Address</Label>
                <Input
                  id="profile-email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  className="h-10 border-slate-200 focus:border-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Role</p>
                  <p className="text-sm font-semibold text-slate-700 capitalize">{getRoleDisplayName(user?.role)}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Username</p>
                  <p className="text-sm font-semibold text-slate-700">@{user?.username}</p>
                </div>
              </div>

              <Button
                onClick={handleProfileUpdate}
                disabled={isUpdatingProfile}
                className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-sm"
              >
                {isUpdatingProfile ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Saving Changes...
                  </>
                ) : (
                  'Update Profile'
                )}
              </Button>
            </div>
          </section>

          {/* Security Section */}
          <section className="space-y-4 pt-2">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
              <Shield className="w-4 h-4 text-orange-600" />
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Security</h3>
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="current_password" className="text-xs font-semibold text-slate-500 mb-1.5 block">Current Password</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    className="h-10 pr-10 border-slate-200 focus:border-orange-500"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="new_password" className="text-xs font-semibold text-slate-500 mb-1.5 block">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new_password"
                      type={showNewPassword ? "text" : "password"}
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      className="h-10 pr-10 border-slate-200 focus:border-orange-500"
                      placeholder="New password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm_password" className="text-xs font-semibold text-slate-500 mb-1.5 block">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm_password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      className="h-10 pr-10 border-slate-200 focus:border-orange-500"
                      placeholder="Confirm password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <Button
                onClick={handlePasswordChange}
                disabled={isChangingPassword}
                variant="outline"
                className="w-full h-10 border-orange-200 text-orange-700 hover:bg-orange-50 hover:text-orange-800 font-medium"
              >
                {isChangingPassword ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Updating Password...
                  </>
                ) : (
                  'Change Password'
                )}
              </Button>
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Enhanced Overview Tab
function OverviewTab() {
  const { user } = React.useContext(AuthContext);
  const [analytics, setAnalytics] = useState(null);
  const [userOverviews, setUserOverviews] = useState([]);
  const [totalUserOverviews, setTotalUserOverviews] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [filters, setFilters] = useState({ startDate: '', endDate: '', role: 'all' });
  const [loading, setLoading] = useState(true);
  const [exportFormat, setExportFormat] = useState('excel');
  const [exportPreset, setExportPreset] = useState('custom');

  // Add state for image viewing
  const [showDetailedActivities, setShowDetailedActivities] = useState(false);
  const [detailedActivityType, setDetailedActivityType] = useState('');
  const [detailedActivities, setDetailedActivities] = useState([]);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role !== 'user') {
      fetchUserOverviews();
    }
    fetchAnalytics();
  }, [user, filters, pageIndex, pageSize]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(totalUserOverviews / pageSize));
    setPageIndex((p) => Math.min(p, totalPages - 1));
  }, [totalUserOverviews, pageSize]);

  const fetchUserOverviews = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.role && filters.role !== 'all') params.append('role', filters.role);
      params.append('page', String(pageIndex + 1));
      params.append('page_size', String(pageSize));

      const response = await api.get(`/api/overview/users?${params}`);
      const payload = response.data || {};
      setUserOverviews(payload.items || []);
      setTotalUserOverviews(payload.total || 0);
    } catch (error) {
      toast.error('Failed to load team overview');
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get(`/api/analytics/dashboard`);
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Refresh overview data
      fetchUserOverviews();
      // Refresh business metrics
      const analyticsResponse = await api.get(`/api/analytics/dashboard`);
      setAnalytics(analyticsResponse.data);
      toast.success('Dashboard data refreshed successfully!');
    } catch (error) {
      toast.error('Failed to refresh dashboard data');
    }
  };

  const handleCardClick = async (activityType) => {
    try {
      setDetailedActivityType(activityType);
      let endpoint = '';

      switch (activityType) {
        case 'recharges':
          endpoint = '/business-activities';
          break;
        case 'new_cards':
          endpoint = '/business-activities';
          break;
        case 'lost_cards':
          endpoint = '/business-activities';
          break;
        case 'replaced_cards':
          endpoint = '/business-activities';
          break;
        default:
          endpoint = '/business-activities';
      }

      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('page_size', '100');
      const response = await api.get(`/api${endpoint}?${params}`);
      const payload = response.data || {};
      setDetailedActivities(payload.items || []);
      setShowDetailedActivities(true);

    } catch (error) {
      toast.error('Failed to load detailed activities');
    }
  };

  const handleImageClick = (images, startIndex = 0, location) => {
    // Check for actual image data
    let imageData = [];

    if (location?.visit_images && location.visit_images.length > 0) {
      imageData = location.visit_images;
    } else if (images && images.length > 0) {
      imageData = images;
    }

    if (imageData && imageData.length > 0) {
      setSelectedImages(imageData);
      setCurrentImageIndex(startIndex);
      setShowImageDialog(true);
      toast.success(`Opening ${imageData.length} photo(s)`);
    } else {
      // Create a demo image URL for testing if no images available
      const demoImages = [
        'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkxvY2F0aW9uIFZpc2l0IFBob3RvPC90ZXh0Pjwvc3ZnPg=='
      ];
      setSelectedImages(demoImages);
      setCurrentImageIndex(0);
      setShowImageDialog(true);
      toast.info('Demo photo view (actual photos will appear once uploaded)');
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % selectedImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + selectedImages.length) % selectedImages.length);
  };

  const renderMachineDetails = (machineDetails) => {
    const md = machineDetails || {};
    const hasAny = Object.values(md).some((v) => v && String(v).trim() !== '');
    if (!hasAny) return <span className="text-gray-400">-</span>;

    return (
      <div className="text-xs text-gray-700 leading-5 whitespace-nowrap">
        {md.school_name && <div>School name : {md.school_name}</div>}
        {md.school_id && <div>School Id: {md.school_id}</div>}
        {md.house_name && <div>House name: {md.house_name}</div>}
        {md.box_no && <div>Box No: {md.box_no}</div>}
        {md.imei_no && <div>IMEI No : {md.imei_no}</div>}
        {md.sim_no && <div>Sim No: {md.sim_no}</div>}
        {md.mobile && <div>Mobile : {md.mobile}</div>}
        {md.network_percent && <div>Network % : {md.network_percent}</div>}
        {md.bandwidth && <div>Bandwidth: {md.bandwidth}</div>}
        {md.ppa_installed_outside && <div>PPA installed outside: {md.ppa_installed_outside}</div>}
      </div>
    );
  };

  const handleUserClick = (userId) => {
    // Navigate to the unified user profile route
    navigate(`/users/${userId}`);
  };

  const handleEditUser = async (userOverview) => {
    // Create a simple edit dialog using prompt for now
    const newName = prompt(`Edit full name for ${userOverview.full_name}:`, userOverview.full_name);
    if (newName && newName !== userOverview.full_name) {
      try {
        await api.put(`/api/users/${userOverview.user_id}`, {
          full_name: newName,
          email: userOverview.email || '',
          position: userOverview.position || ''
        });
        toast.success('User profile updated successfully!');
        fetchUserOverviews(); // Refresh the list
      } catch (error) {
        toast.error('Failed to update user profile');
      }
    }
  };

  const handleDeleteUser = async (userOverview) => {
    if (window.confirm(`Are you sure you want to delete ${userOverview.full_name}? This action cannot be undone.`)) {
      try {
        await api.delete(`/api/users/${userOverview.user_id}`);
        toast.success('User deleted successfully!');
        fetchUserOverviews(); // Refresh the list
      } catch (error) {
        toast.error('Failed to delete user');
      }
    }
  };

  const handleToggleUserStatus = async (userOverview) => {
    const action = userOverview.is_active ? 'deactivate' : 'activate';
    if (window.confirm(`Are you sure you want to ${action} ${userOverview.full_name}?`)) {
      try {
        await api.put(`/api/users/${userOverview.user_id}/status`, {
          is_active: !userOverview.is_active
        });
        toast.success(`User ${action}d successfully!`);
        fetchUserOverviews(); // Refresh the list
      } catch (error) {
        toast.error(`Failed to ${action} user`);
      }
    }
  };

  const clearFilters = () => {
    setFilters({ startDate: '', endDate: '', role: 'all' });
  };

  const downloadFile = (filename, content, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const getTeamOverviewExportRows = () => {
    return (userOverviews || []).map((u) => ({
      full_name: u.full_name || '',
      username: u.username || '',
      role: u.role || '',
      total_expenses: u.total_expenses ?? 0,
      total_expense_amount: u.total_expense_amount ?? 0,
      // total_business_activities: u.total_business_activities ?? 0,
      // total_recharges: u.total_recharges ?? 0,
      total_location_visits: u.total_location_visits ?? 0,
      last_activity: u.last_activity ? new Date(u.last_activity).toLocaleDateString() : ''
    }));
  };

  const exportTeamOverview = () => {
    const rows = getTeamOverviewExportRows();
    if (!rows.length) {
      toast.error('No data to export');
      return;
    }

    const start = filters.startDate || 'all';
    const end = filters.endDate || 'all';
    const baseName = `team_overview_${start}_to_${end}`;

    if (exportFormat === 'csv') {
      const headers = Object.keys(rows[0]);
      const csv = [
        headers.join(','),
        ...rows.map((r) => headers.map((h) => `"${String(r[h] ?? '').replaceAll('"', '""')}"`).join(','))
      ].join('\n');
      downloadFile(`${baseName}.csv`, csv, 'text/csv;charset=utf-8');
      toast.success('Report downloaded');
      return;
    }

    const headers = Object.keys(rows[0]);
    const table = [
      '<table>',
      '<thead><tr>' + headers.map((h) => `<th>${h}</th>`).join('') + '</tr></thead>',
      '<tbody>',
      ...rows.map((r) => '<tr>' + headers.map((h) => `<td>${String(r[h] ?? '')}</td>`).join('') + '</tr>'),
      '</tbody>',
      '</table>'
    ].join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"></head><body>${table}</body></html>`;
    downloadFile(`${baseName}.xls`, html, 'application/vnd.ms-excel');
    toast.success('Report downloaded');
  };

  const applyExportPreset = (preset) => {
    setExportPreset(preset);
    if (preset === 'custom') return;
    const today = new Date();
    const toIso = (d) => d.toISOString().split('T')[0];
    let startDate = '';
    let endDate = toIso(today);
    if (preset === 'today') {
      startDate = toIso(today);
    } else if (preset === 'last7') {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      startDate = toIso(start);
    } else if (preset === 'last30') {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      startDate = toIso(start);
    }
    setFilters((prev) => ({ ...prev, startDate, endDate }));
  };

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'supervisor_admin': 'Supervisor',
      'admin': 'Admin',
      'moderator': 'Moderator',
      'user': 'Staff'
    };
    return roleMap[role] || role;
  };

  // Calculate pagination values
  const totalPages = Math.max(1, Math.ceil(totalUserOverviews / pageSize));
  const safePageIndex = Math.min(pageIndex, totalPages - 1);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Dashboard Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Dashboard Overview</h1>
          <p className="text-sm text-slate-500 mt-1">
            Welcome back, {user?.full_name || 'User'}! Here's your business performance summary.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden md:flex text-xs font-medium text-slate-500 bg-slate-50 px-3 py-1.5 rounded-full border border-slate-200 items-center">
            <Calendar className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
          <Button size="sm" onClick={fetchDashboardData} variant="outline" className="h-9 border-slate-200 hover:bg-slate-50 text-slate-600">
            <Activity className="w-3.5 h-3.5 mr-2" />
            Refresh Data
          </Button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Recharges */}
        {/* <Card
          className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden group cursor-pointer"
          onClick={() => handleCardClick('recharges')}
        >
          <CardContent className="p-5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Total Recharges</p>
                <h3 className="text-2xl font-bold text-slate-900 mt-2">
                  ₹{analytics?.business_summary?.total_recharges?.toFixed(2) || '0.00'}
                </h3>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                <PhoneCall className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-xs text-blue-600 font-medium group-hover:underline">
              View Details
            </div>
          </CardContent>
        </Card> */}

        {/* New Cards */}
        {/* <Card
          className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden group cursor-pointer"
          onClick={() => handleCardClick('new_cards')}
        >
          <CardContent className="p-5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">New Cards</p>
                <h3 className="text-2xl font-bold text-slate-900 mt-2">
                  {analytics?.business_summary?.total_new_cards || 0}
                </h3>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                <CreditCard className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-xs text-blue-600 font-medium group-hover:underline">
              View Details
            </div>
          </CardContent>
        </Card> */}

        {/* Lost/Swap Cards */}
        {/* <Card
          className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden group cursor-pointer"
          onClick={() => handleCardClick('lost_cards')}
        >
          <CardContent className="p-5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Lost/Swap Cards</p>
                <h3 className="text-2xl font-bold text-slate-900 mt-2">
                  {((analytics?.business_summary?.total_lost_cards || 0) + (analytics?.business_summary?.total_replaced_cards || 0))}
                </h3>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-xs text-blue-600 font-medium group-hover:underline">
              View Details
            </div>
          </CardContent>
        </Card> */}

        {/* Active Team Members (calculated from userOverviews) */}
        <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden group">
          <CardContent className="p-5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Team Activity</p>
                <h3 className="text-2xl font-bold text-slate-900 mt-2">
                  {totalUserOverviews || 0}
                </h3>
              </div>
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 transition-colors">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
            </div>
            <div className="mt-4 text-xs text-slate-400">
              Active users in system
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Complete Team Overview Table */}
      {user?.role !== 'user' && (
        <Card className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
          <CardHeader className="bg-white border-b border-slate-100 px-6 py-5">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <CardTitle className="text-lg font-bold text-slate-800 flex items-center">
                  <Users className="w-5 h-5 mr-2 text-blue-600" />
                  Team Overview
                </CardTitle>
                <CardDescription className="text-slate-500 mt-1">
                  Performance metrics and activity log for all team members
                </CardDescription>
              </div>

              {/* Advanced Filtering Options */}
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">From</label>
                  <Input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => {
                      setFilters({ ...filters, startDate: e.target.value });
                      setPageIndex(0);
                    }}
                    className="h-9 w-36 text-xs border-slate-200 bg-white"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">To</label>
                  <Input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => {
                      setFilters({ ...filters, endDate: e.target.value });
                      setPageIndex(0);
                    }}
                    className="h-9 w-36 text-xs border-slate-200 bg-white"
                  />
                </div>

                <Select
                  value={filters.role}
                  onValueChange={(value) => {
                    setFilters({ ...filters, role: value });
                    setPageIndex(0);
                  }}
                >
                  <SelectTrigger className="w-32 h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="user">Staff</SelectItem>
                    <SelectItem value="moderator">Moderator</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>

                <div className="h-6 w-px bg-slate-200 mx-1"></div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 w-9 p-0">
                      <Download className="w-4 h-4 text-slate-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Export Data</DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => { setExportFormat('excel'); exportTeamOverview(); }}>
                      Export as Excel
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { setExportFormat('csv'); exportTeamOverview(); }}>
                      Export as CSV
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  onClick={() => {
                    setFilters({ startDate: '', endDate: '', role: 'all' });
                    setPageIndex(0);
                    fetchUserOverviews();
                    toast.success('Filters cleared');
                  }}
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0 text-slate-500 hover:text-blue-600"
                  title="Reset Filters"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {userOverviews.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 bg-slate-50/50">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-8 h-8 text-slate-300" />
                </div>
                <h3 className="text-slate-900 font-semibold">No Team Data Found</h3>
                <p className="text-slate-500 text-sm mt-1 mb-6">Try adjusting your filters or add new team members.</p>
                <Button onClick={clearFilters} variant="outline">Clear Filters</Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80 border-b border-slate-200 hover:bg-slate-50/50">
                      <TableHead className="w-[200px] text-[11px] uppercase font-bold text-slate-500 tracking-wider">Team Member</TableHead>
                      <TableHead className="text-center text-[11px] uppercase font-bold text-slate-500 tracking-wider">Role</TableHead>
                      <TableHead className="text-center text-[11px] uppercase font-bold text-slate-500 tracking-wider">Total Expenses</TableHead>
                      <TableHead className="text-center text-[11px] uppercase font-bold text-slate-500 tracking-wider">Recharges</TableHead>
                      <TableHead className="text-center text-[11px] uppercase font-bold text-slate-500 tracking-wider">Visits</TableHead>
                      <TableHead className="text-center text-[11px] uppercase font-bold text-slate-500 tracking-wider">Last Activity</TableHead>
                      <TableHead className="text-right text-[11px] uppercase font-bold text-slate-500 tracking-wider">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {userOverviews.map((userOverview) => (
                      <TableRow
                        key={userOverview.user_id}
                        className="group hover:bg-blue-50/20 transition-colors border-b border-slate-100"
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-3" onClick={() => handleUserClick(userOverview.user_id)}>
                            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold ring-2 ring-white shadow-sm">
                              {userOverview.full_name?.charAt(0) || 'U'}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-700 group-hover:text-blue-600 transition-colors cursor-pointer">{userOverview.full_name}</p>
                              <p className="text-[11px] text-slate-400">@{userOverview.username}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary" className="bg-slate-100 text-slate-500 font-normal hover:bg-slate-200">
                            {getRoleDisplayName(userOverview.role)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center">
                            <span className="font-bold text-slate-700">₹{userOverview.total_expense_amount?.toLocaleString() || '0'}</span>
                            <span className="text-[10px] text-slate-400">{userOverview.total_expenses} records</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="font-medium text-emerald-600">₹{userOverview.total_recharges?.toLocaleString() || '0'}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="font-medium text-purple-600">{userOverview.total_location_visits}</span>
                        </TableCell>
                        <TableCell className="text-center text-xs text-slate-500">
                          {userOverview.last_activity ? new Date(userOverview.last_activity).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-[180px]">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuItem onClick={() => handleUserClick(userOverview.user_id)}>
                                <User className="w-3.5 h-3.5 mr-2 text-slate-500" /> View Profile
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => window.dispatchEvent(new CustomEvent('navigateToUserExpenses', { detail: userOverview.user_id }))}>
                                <Receipt className="w-3.5 h-3.5 mr-2 text-slate-500" /> View Expenses
                              </DropdownMenuItem>

                              {/* Admin Actions */}
                              {user?.role === 'supervisor_admin' && (
                                <>
                                  <div className="h-px bg-slate-100 my-1"></div>
                                  <DropdownMenuItem onClick={() => handleEditUser(userOverview)}>
                                    <Edit className="w-3.5 h-3.5 mr-2 text-blue-500" /> Edit User
                                  </DropdownMenuItem>
                                  <DropdownMenuItem className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteUser(userOverview)}>
                                    <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {/* Pagination Footer */}
            {userOverviews.length > 0 && (
              <div className="border-t border-slate-100 p-4 flex items-center justify-between bg-slate-50/30">
                <div className="text-xs text-slate-500">
                  Showing <strong>{pageIndex * pageSize + 1}</strong> to <strong>{Math.min((pageIndex + 1) * pageSize, totalUserOverviews)}</strong> of <strong>{totalUserOverviews}</strong> members
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPageIndex(p => Math.max(0, p - 1))} disabled={pageIndex === 0} className="h-8">Previous</Button>
                  <Button variant="outline" size="sm" onClick={() => setPageIndex(p => Math.min(totalPages - 1, p + 1))} disabled={pageIndex >= totalPages - 1} className="h-8">Next</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Detailed Activities Dialog */}
      <Dialog open={showDetailedActivities} onOpenChange={setShowDetailedActivities}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              <span>Detailed {detailedActivityType.replace('_', ' ').toUpperCase()} Activities</span>
            </DialogTitle>
            <DialogDescription>
              Complete list of all {detailedActivityType.replace('_', ' ')} activities across your team
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {detailedActivities.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Date</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Daily Recharges</TableHead>
                      <TableHead>New Cards</TableHead>
                      <TableHead>Lost/Swap Cards</TableHead>
                      <TableHead>Other Activities</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detailedActivities.map((activity, index) => (
                      <TableRow key={index} className="hover:bg-gray-50">
                        <TableCell className="font-medium">
                          {activity.date ? new Date(activity.date).toLocaleDateString() : new Date(activity.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="font-semibold text-blue-600">
                          {activity.user_name || 'Unknown User'}
                        </TableCell>
                        <TableCell className="font-bold text-green-600">
                          ₹{activity.daily_recharges?.toFixed(2) || '0.00'}
                        </TableCell>
                        <TableCell className="text-blue-600">{activity.new_cards || 0}</TableCell>
                        <TableCell className="text-orange-600">
                          {(activity.lost_cards || 0) + (activity.replaced_cards || 0)}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">{activity.other_activities || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-16">
                <BarChart3 className="w-20 h-20 text-gray-300 mx-auto mb-6" />
                <p className="text-gray-500 text-xl font-semibold mb-2">No Activities Found</p>
                <p className="text-gray-400">No {detailedActivityType.replace('_', ' ')} activities recorded yet</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailedActivities(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Machine Details Defaults Tab
function MachineDetailsDefaultsTab() {
  const { user } = React.useContext(AuthContext);
  const isSupervisorAdmin = user?.role === 'supervisor_admin';
  const today = new Date().toISOString().split('T')[0];

  // Common details for the school visit (date and school_name only)
  const [commonDetails, setCommonDetails] = useState({
    date: today,
    school_name: ''
  });

  // Dynamic list of machine entries (school_id and house_name are per-machine)
  const [machineEntries, setMachineEntries] = useState([
    {
      id: Date.now(),
      school_id: '',
      house_name: '',
      box_no: '',
      imei_no: '',
      sim_no: '',
      mobile: '',
      network_percent: '',
      bandwidth: '',
      bandwidthSelect: '',
      ppa_installed_outside: ''
    }
  ]);

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    search: ''
  });
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [showFilterDialog, setShowFilterDialog] = useState(false);

  // Grouped View States
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [viewingSchoolGroup, setViewingSchoolGroup] = useState(null);

  const recordsKey = user?.id ? `machineDetailsRecords_${user.id}` : 'machineDetailsRecords_guest';
  const [savedRecords, setSavedRecords] = useState([]);
  const [editingRecordId, setEditingRecordId] = useState(null);

  // Group toggle state
  const [expandedSchool, setExpandedSchool] = useState(null);

  // Group records by school_name
  const groupedRecords = React.useMemo(() => {
    return savedRecords.reduce((acc, record) => {
      const schoolName = record.school_name || 'Unknown School';
      if (!acc[schoolName]) {
        acc[schoolName] = [];
      }
      acc[schoolName].push(record);
      return acc;
    }, {});
  }, [savedRecords]);

  const toggleSchool = (schoolName) => {
    setExpandedSchool(expandedSchool === schoolName ? null : schoolName);
  };

  useEffect(() => {
    try {
      const saved = localStorage.getItem(recordsKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        setSavedRecords(Array.isArray(parsed) ? parsed : []);
      } else {
        setSavedRecords([]);
      }
    } catch (error) {
      setSavedRecords([]);
    }
  }, [recordsKey]);

  // Handle changes for common fields
  const handleCommonChange = (field, value) => {
    setCommonDetails((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle changes for machine-specific fields
  const handleMachineChange = (id, field, value) => {
    setMachineEntries((prev) => prev.map(entry => {
      if (entry.id === id) {
        const updated = { ...entry, [field]: value };
        // Special handling for bandwidth select logic inside the entry
        if (field === 'bandwidthSelect') {
          if (value === 'others') {
            if (BANDWIDTH_OPTIONS.includes(entry.bandwidth)) {
              updated.bandwidth = '';
            }
          } else {
            updated.bandwidth = value;
          }
        }
        return updated;
      }
      return entry;
    }));
  };

  const addMachineEntry = () => {
    setMachineEntries(prev => [
      ...prev,
      {
        id: Date.now(),
        school_id: '',
        house_name: '',
        box_no: '',
        imei_no: '',
        sim_no: '',
        mobile: '',
        network_percent: '',
        bandwidth: '',
        bandwidthSelect: '',
        ppa_installed_outside: ''
      }
    ]);
  };

  const removeMachineEntry = (id) => {
    if (machineEntries.length > 1) {
      setMachineEntries(prev => prev.filter(e => e.id !== id));
    }
  };

  const startEditRecord = (record) => {
    if (!record) return;
    setEditingRecordId(record._id || null);

    // Set common details (only date and school_name)
    setCommonDetails({
      date: record.date || today,
      school_name: record.school_name || ''
    });

    // Set single machine entry for editing with school_id and house_name per-machine
    setMachineEntries([{
      id: Date.now(),
      school_id: record.school_id || '',
      house_name: record.house_name || '',
      box_no: record.box_no || '',
      imei_no: record.imei_no || '',
      sim_no: record.sim_no || '',
      mobile: record.mobile || '',
      network_percent: record.network_percent || '',
      bandwidth: record.bandwidth || '',
      bandwidthSelect: getBandwidthSelectValue(record.bandwidth),
      ppa_installed_outside: record.ppa_installed_outside || ''
    }]);
  };

  const getGpsWithTimeout = (timeoutMs = 1500) => {
    if (!navigator.geolocation) return Promise.resolve(null);
    return new Promise((resolve) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        resolve(null);
      }, timeoutMs);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date().toISOString(),
            google_maps_url: `https://maps.google.com/maps?q=${position.coords.latitude},${position.coords.longitude}`
          });
        },
        () => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 300000 }
      );
    });
  };

  const handleSaveDefaults = async () => {
    // Validate if at least one entry has some data
    const hasAnyChain = machineEntries.some(entry =>
      entry.box_no || entry.imei_no || entry.sim_no || entry.mobile ||
      entry.school_id || entry.house_name || commonDetails.school_name
    );

    if (!hasAnyChain) {
      toast.error('Please fill at least one field before saving');
      return;
    }

    try {
      const timestamp = new Date().toISOString();
      let newRecords = [];
      let gpsLocation = null;

      // Get GPS once for all entries in this batch
      if (!editingRecordId) {
        gpsLocation = await getGpsWithTimeout();
      }

      const submissionUser = user ? { id: user.id, username: user.username, role: user.role } : null;

      for (const entry of machineEntries) {
        // Skip completely empty entries if there are multiple
        const isEntryEmpty = !entry.box_no && !entry.imei_no && !entry.sim_no && !entry.mobile;
        if (machineEntries.length > 1 && isEntryEmpty) continue;

        const existingRecord = editingRecordId
          ? savedRecords.find((record) => record._id === editingRecordId)
          : null;

        const recordId = editingRecordId || `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Use existing GPS if editing, otherwise use new GPS
        const recordGps = existingRecord?._gps_location ?? gpsLocation;

        const newRecord = {
          _id: recordId,
          _saved_at: timestamp,
          _gps_location: recordGps,
          _submitted_by: existingRecord?._submitted_by || submissionUser,

          // Common Details
          date: commonDetails.date,
          school_name: commonDetails.school_name,

          // Per-Machine Details (school_id and house_name can differ per machine)
          school_id: entry.school_id,
          house_name: entry.house_name,
          box_no: entry.box_no,
          imei_no: entry.imei_no,
          sim_no: entry.sim_no,
          mobile: entry.mobile,
          network_percent: entry.network_percent,
          bandwidth: entry.bandwidth,
          ppa_installed_outside: entry.ppa_installed_outside
        };
        newRecords.push(newRecord);
      }

      if (newRecords.length === 0) {
        toast.error('No valid data to save');
        return;
      }

      let updatedRecords;
      if (editingRecordId) {
        // If editing, we replace the single record (we restricted editing to single mode)
        updatedRecords = savedRecords.map((record) => (record._id === editingRecordId ? newRecords[0] : record));
        toast.success('Machine details updated');
      } else {
        updatedRecords = [...newRecords, ...savedRecords];
        toast.success(`${newRecords.length} machine detail(s) saved`);
        if (gpsLocation) toast.info('Location captured');
      }

      localStorage.setItem(recordsKey, JSON.stringify(updatedRecords));
      setSavedRecords(updatedRecords);

      // Reset form
      handleClearDefaults();

    } catch (error) {
      toast.error('Failed to save machine details');
      console.error(error);
    }
  };

  const handleDeleteRecord = (id) => {
    const updated = savedRecords.filter((r) => r._id !== id);
    setSavedRecords(updated);
    localStorage.setItem(recordsKey, JSON.stringify(updated));
    if (editingRecordId === id) {
      setEditingRecordId(null);
    }
    toast.success('Record removed');

    // Also update grouped view if open
    if (viewingSchoolGroup) {
      const remainingInGroup = updated.filter(r => r.school_name === viewingSchoolGroup.school_name);
      if (remainingInGroup.length === 0) {
        setShowGroupDialog(false);
        setViewingSchoolGroup(null);
      } else {
        setViewingSchoolGroup({ ...viewingSchoolGroup, records: remainingInGroup });
      }
    }
  };

  const handleClearDefaults = () => {
    setCommonDetails({
      date: today,
      school_name: ''
    });
    setMachineEntries([{
      id: Date.now(),
      school_id: '',
      house_name: '',
      box_no: '',
      imei_no: '',
      sim_no: '',
      mobile: '',
      network_percent: '',
      bandwidth: '',
      bandwidthSelect: '',
      ppa_installed_outside: ''
    }]);
    setEditingRecordId(null);
    toast.success('Inputs cleared');
  };

  const handleViewSchoolGroup = (record) => {
    // Find all records with same school name/id
    const schoolName = record.school_name;
    if (!schoolName) return;

    const group = savedRecords.filter(r => r.school_name === schoolName);
    setViewingSchoolGroup({
      school_name: schoolName,
      school_id: record.school_id,
      date: record.date, // Use date from clicked record
      records: group
    });
    setShowGroupDialog(true);
  };

  return (
    <div className="md:p-8 bg-slate-50 min-h-screen font-sans">
      <div className="max-w-[1400px] mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-lg">
                <Activity className="w-6 h-6 text-indigo-600" />
              </div>
              Machine Details Configuration
            </h2>
            <p className="text-slate-500 mt-1 ml-11">
              Set default machine details for upcoming location visits
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setShowFilterDialog(true)}
              className="bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filter Records
            </Button>
          </div>
        </div>

        {/* Input Form Card */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-slate-50/30">
            <div>
              <h3 className="text-lg font-bold text-slate-800">New Entry Details</h3>
              <p className="text-sm text-slate-500">Enter school and machine information</p>
            </div>
            <div className="flex gap-3">
              {editingRecordId && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    handleClearDefaults();
                    setEditingRecordId(null);
                  }}
                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                >
                  Cancel Edit
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleClearDefaults}
                className="border-slate-200 text-slate-600 hover:text-slate-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset
              </Button>
              <Button
                onClick={handleSaveDefaults}
                className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-100"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingRecordId ? 'Update Record' : 'Save Details'}
              </Button>
            </div>
          </div>

          <div className="p-6 space-y-8">
            {/* Common Details Section */}
            <section>
              <div className="flex items-center gap-2 mb-6 text-indigo-900/80 font-medium pb-2 border-b border-indigo-50">
                <Building2 className="w-5 h-5" />
                <span>School Information</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</Label>
                  <Input
                    type="date"
                    value={commonDetails.date}
                    onChange={(e) => handleCommonChange('date', e.target.value)}
                    className="h-10 border-slate-200 focus:border-indigo-300 focus:ring focus:ring-indigo-100"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">School Name</Label>
                  <Input
                    value={commonDetails.school_name}
                    onChange={(e) => handleCommonChange('school_name', e.target.value)}
                    placeholder="Enter school name"
                    className="h-10 border-slate-200 focus:border-indigo-300 focus:ring focus:ring-indigo-100"
                  />
                </div>
              </div>
            </section>

            {/* Machine Entries Section */}
            <section>
              <div className="flex items-center justify-between mb-6 pb-2 border-b border-indigo-50">
                <div className="flex items-center gap-2 text-indigo-900/80 font-medium">
                  <Cpu className="w-5 h-5" />
                  <span>Machine Information</span>
                </div>
                {!editingRecordId && (
                  <Button
                    variant="outline"
                    onClick={addMachineEntry}
                    className="text-indigo-600 border-indigo-100 hover:bg-indigo-50"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Machine
                  </Button>
                )}
              </div>

              <div className="space-y-4">
                {machineEntries.map((entry, index) => (
                  <div
                    key={entry.id}
                    className="relative bg-slate-50 rounded-xl border border-slate-200 p-5 transition-all hover:border-indigo-200 group"
                  >
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500 rounded-l-xl opacity-0 group-hover:opacity-100 transition-opacity" />

                    <div className="flex items-center gap-2 mb-4">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold">
                        {index + 1}
                      </span>
                      <span className="text-sm font-medium text-slate-700">Machine Entry</span>

                      {machineEntries.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeMachineEntry(entry.id)}
                          className="ml-auto text-slate-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0 rounded-full"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">School ID</Label>
                        <Input
                          value={entry.school_id}
                          onChange={(e) => handleMachineChange(entry.id, 'school_id', e.target.value)}
                          placeholder="Enter ID"
                          className="h-9 text-sm bg-white border-slate-200 focus:border-indigo-300 font-mono"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">House Name</Label>
                        <Input
                          value={entry.house_name}
                          onChange={(e) => handleMachineChange(entry.id, 'house_name', e.target.value)}
                          placeholder="Enter house name"
                          className="h-9 text-sm bg-white border-slate-200 focus:border-indigo-300"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">Box No</Label>
                        <Input
                          value={entry.box_no}
                          onChange={(e) => handleMachineChange(entry.id, 'box_no', e.target.value)}
                          placeholder="Box Number"
                          className="h-9 text-sm bg-white border-slate-200 focus:border-indigo-300"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">IMEI No</Label>
                        <Input
                          value={entry.imei_no}
                          onChange={(e) => handleMachineChange(entry.id, 'imei_no', e.target.value)}
                          placeholder="IMEI Number"
                          className="h-9 text-sm bg-white border-slate-200 focus:border-indigo-300 font-mono"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">SIM No</Label>
                        <Input
                          value={entry.sim_no}
                          onChange={(e) => handleMachineChange(entry.id, 'sim_no', e.target.value)}
                          placeholder="SIM Number"
                          className="h-9 text-sm bg-white border-slate-200 focus:border-indigo-300"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">Mobile No</Label>
                        <Input
                          value={entry.mobile}
                          onChange={(e) => handleMachineChange(entry.id, 'mobile', e.target.value)}
                          placeholder="Mobile Number"
                          className="h-9 text-sm bg-white border-slate-200 focus:border-indigo-300"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">Network %</Label>
                        <Input
                          type="number"
                          value={entry.network_percent}
                          onChange={(e) => handleMachineChange(entry.id, 'network_percent', e.target.value)}
                          placeholder="%"
                          className="h-9 text-sm bg-white border-slate-200 focus:border-indigo-300"
                        />
                      </div>
                      <div className="space-y-1.5 md:col-span-2">
                        <Label className="text-xs text-slate-500">Bandwidth</Label>
                        <div className="flex gap-2">
                          <Select
                            value={entry.bandwidthSelect}
                            onValueChange={(value) => handleMachineChange(entry.id, 'bandwidthSelect', value)}
                          >
                            <SelectTrigger className="h-9 w-[140px] text-sm bg-white">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {BANDWIDTH_OPTIONS.map((option) => (
                                <SelectItem key={option} value={option}>{option}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {entry.bandwidthSelect === 'others' ? (
                            <Input
                              value={entry.bandwidth}
                              onChange={(e) => handleMachineChange(entry.id, 'bandwidth', e.target.value)}
                              placeholder="Specify"
                              className="h-9 flex-1 text-sm bg-white"
                            />
                          ) : (
                            <Input
                              value={entry.bandwidth}
                              disabled
                              className="h-9 flex-1 bg-slate-50 text-slate-400 text-sm"
                              placeholder="-"
                            />
                          )}
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">PPA Installed Outside</Label>
                        <Select
                          value={entry.ppa_installed_outside || ''}
                          onValueChange={(value) => handleMachineChange(entry.id, 'ppa_installed_outside', value)}
                        >
                          <SelectTrigger className="h-9 text-sm bg-white">
                            <SelectValue placeholder="Select option" />
                          </SelectTrigger>
                          <SelectContent>
                            {PPA_OPTIONS.map((option) => (
                              <SelectItem key={option} value={option}>{option}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>

        {/* Saved Records Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Saved Records</h3>
              <p className="text-sm text-slate-500">
                {savedRecords.length} record(s) found
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search records..."
                  value={filters.search}
                  onChange={(e) => {
                    setFilters({ ...filters, search: e.target.value });
                    setPageIndex(0);
                  }}
                  className="pl-10 h-9 w-[200px] lg:w-[300px] border-slate-200 text-sm"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50 hover:bg-slate-50/50 border-b border-slate-100">
                  <TableHead className="font-semibold text-slate-500">Date</TableHead>
                  <TableHead className="font-semibold text-slate-500">School Name</TableHead>
                  <TableHead className="font-semibold text-slate-500">School ID</TableHead>
                  <TableHead className="font-semibold text-slate-500">Box No</TableHead>
                  <TableHead className="font-semibold text-slate-500">IMEI</TableHead>
                  <TableHead className="font-semibold text-slate-500">Network</TableHead>
                  {(user?.role === 'supervisor_admin' || user?.role === 'admin' || user?.role === 'moderator') && (
                    <TableHead className="font-semibold text-slate-500">Submitted By</TableHead>
                  )}
                  <TableHead className="text-right font-semibold text-slate-500">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const filteredRecords = savedRecords.filter((record) => {
                    if (filters.search) {
                      const searchLower = filters.search.toLowerCase();
                      const haystack = Object.entries(record)
                        .filter(([k]) => !k.startsWith('_'))
                        .map(([, v]) => String(v || ''))
                        .join(' ')
                        .toLowerCase();
                      if (!haystack.includes(searchLower)) return false;
                    }
                    if (filters.startDate && record._saved_at) {
                      const recordDate = new Date(record._saved_at).toISOString().split('T')[0];
                      if (recordDate < filters.startDate) return false;
                    }
                    if (filters.endDate && record._saved_at) {
                      const recordDate = new Date(record._saved_at).toISOString().split('T')[0];
                      if (recordDate > filters.endDate) return false;
                    }
                    return true;
                  });

                  // Group filtered records by school name
                  const groupedMap = filteredRecords.reduce((acc, record) => {
                    const schoolName = record.school_name || 'Unknown School';
                    if (!acc[schoolName]) {
                      acc[schoolName] = [];
                    }
                    acc[schoolName].push(record);
                    return acc;
                  }, {});

                  const schoolGroups = Object.keys(groupedMap).map(schoolName => ({
                    schoolName,
                    records: groupedMap[schoolName]
                  }));

                  // Helper logic for sorting schools by most recent visit if needed
                  // currently just iterating

                  if (schoolGroups.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={8} className="h-48 text-center text-slate-400">
                          <div className="flex flex-col items-center justify-center">
                            <FileText className="w-12 h-12 mb-3 opacity-20" />
                            <p>No records found</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  // Paginate groups
                  const totalGroups = schoolGroups.length;
                  const startIdx = pageIndex * pageSize;
                  const endIdx = startIdx + pageSize;
                  const pageGroups = schoolGroups.slice(startIdx, endIdx);

                  return pageGroups.map(({ schoolName, records }) => {
                    const latestRecord = records[0];
                    return (
                      <React.Fragment key={schoolName}>
                        {/* Parent Row - School Group */}
                        <TableRow
                          onClick={() => toggleSchool(schoolName)}
                          className="hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-100 group"
                        >
                          <TableCell className="font-medium text-slate-500">
                            {new Date(latestRecord.date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center text-slate-800 font-semibold">
                              <Building2 className="w-4 h-4 mr-2 text-indigo-500 group-hover:text-indigo-600 transition-colors" />
                              {schoolName}
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-500 font-mono text-xs">{latestRecord.school_id || '-'}</TableCell>

                          {/* Merged columns for school-level view */}
                          <TableCell colSpan={4} className="text-slate-500 italic text-sm">
                            {records.length} machine details recorded
                          </TableCell>

                          {(user?.role === 'supervisor_admin' || user?.role === 'admin' || user?.role === 'moderator') && (
                            <TableCell>
                              {latestRecord._submitted_by ? (
                                <div className="flex items-center text-xs text-slate-500">
                                  <div className="w-6 h-6 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 mr-2 font-bold">
                                    {latestRecord._submitted_by.username?.[0]?.toUpperCase()}
                                  </div>
                                  {latestRecord._submitted_by.username}
                                </div>
                              ) : '-'}
                            </TableCell>
                          )}

                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewSchoolGroup(latestRecord);
                                }}
                                className="h-8 w-8 p-0 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 group-hover:text-indigo-500">
                                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expandedSchool === schoolName ? 'rotate-180' : ''}`} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>

                        {/* Child Rows - Individual Machine Records */}
                        {expandedSchool === schoolName && records.map((record, idx) => (
                          <TableRow key={record._id || record.id} className="bg-slate-50/50 border-l-4 border-l-indigo-500 animate-in fade-in slide-in-from-top-1 duration-200">
                            <TableCell className="pl-6 text-slate-400 text-xs align-top pt-4">
                              #{idx + 1}
                            </TableCell>
                            <TableCell colSpan={(user?.role === 'supervisor_admin' || user?.role === 'admin' || user?.role === 'moderator') ? 7 : 6}>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-2">
                                <div className="space-y-1">
                                  <span className="text-xs font-semibold text-slate-500 uppercase">School ID</span>
                                  <div className="font-mono text-sm text-slate-700">{record.school_id || '-'}</div>
                                </div>
                                <div className="space-y-1">
                                  <span className="text-xs font-semibold text-slate-500 uppercase">House Name</span>
                                  <div className="text-sm text-slate-700">{record.house_name || '-'}</div>
                                </div>
                                <div className="space-y-1">
                                  <span className="text-xs font-semibold text-slate-500 uppercase">Box No</span>
                                  <div className="font-medium text-slate-900">{record.box_no || '-'}</div>
                                </div>
                                <div className="space-y-1">
                                  <span className="text-xs font-semibold text-slate-500 uppercase">IMEI</span>
                                  <div className="font-mono text-xs text-slate-600">{record.imei_no || '-'}</div>
                                </div>
                                <div className="space-y-1">
                                  <span className="text-xs font-semibold text-slate-500 uppercase">SIM / Mobile</span>
                                  <div className="text-xs text-slate-700">
                                    <div>{record.sim_no || '-'}</div>
                                    <div className="text-slate-500">{record.mobile || '-'}</div>
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <span className="text-xs font-semibold text-slate-500 uppercase">Network</span>
                                  <div>
                                    {record.network_percent ? (
                                      <Badge variant="outline" className={`
                                        ${parseInt(record.network_percent) >= 70 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                          parseInt(record.network_percent) >= 40 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                            'bg-rose-50 text-rose-700 border-rose-200'}
                                      `}>
                                        {record.network_percent}%
                                      </Badge>
                                    ) : '-'}
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <span className="text-xs font-semibold text-slate-500 uppercase">Bandwidth</span>
                                  <div className="text-sm text-slate-700">{record.bandwidth || '-'}</div>
                                </div>
                                <div className="space-y-1">
                                  <span className="text-xs font-semibold text-slate-500 uppercase">PPA Outside</span>
                                  <div className="text-sm text-slate-700">{record.ppa_installed_outside || '-'}</div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right align-top pt-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteRecord(record._id);
                                }}
                                className="text-slate-400 hover:text-rose-600 hover:bg-rose-50 h-7 w-7"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    )
                  });
                })()}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="bg-slate-50/50 p-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <div className="flex items-center gap-2">
              <span>Rows per page:</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPageIndex(0); }}>
                <SelectTrigger className="w-16 h-8 text-xs bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 25, 50, 100].map((size) => (
                    <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={pageIndex === 0}
                onClick={() => setPageIndex(p => p - 1)}
              >
                &lt;
              </Button>
              <span>Page {pageIndex + 1}</span>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={savedRecords.length <= (pageIndex + 1) * pageSize}
                onClick={() => setPageIndex(p => p + 1)}
              >
                &gt;
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Dialog */}
      <Dialog open={showFilterDialog} onOpenChange={setShowFilterDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Filter Records</DialogTitle>
            <DialogDescription>Filter by date range</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFilters({ startDate: '', endDate: '', search: '' })}>
              Clear
            </Button>
            <Button onClick={() => setShowFilterDialog(false)}>
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group View Dialog */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="text-xl font-bold flex items-center text-slate-800">
              <Building2 className="w-5 h-5 mr-2 text-indigo-600" />
              {viewingSchoolGroup?.school_name}
            </DialogTitle>
            <DialogDescription className="text-slate-500 flex gap-4 mt-1">
              <span>ID: {viewingSchoolGroup?.school_id || 'N/A'}</span>
              <span>•</span>
              <span>Date: {viewingSchoolGroup?.date}</span>
              <span>•</span>
              <span>{viewingSchoolGroup?.records?.length} Machine(s)</span>
            </DialogDescription>
          </DialogHeader>

          <div className="py-6">
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader className="bg-slate-50/50">
                  <TableRow>
                    <TableHead className="w-[50px] font-semibold">#</TableHead>
                    <TableHead className="font-semibold">School ID</TableHead>
                    <TableHead className="font-semibold">House Name</TableHead>
                    <TableHead className="font-semibold">Box No</TableHead>
                    <TableHead className="font-semibold">IMEI</TableHead>
                    <TableHead className="font-semibold">SIM / Mobile</TableHead>
                    <TableHead className="font-semibold">Network</TableHead>
                    <TableHead className="font-semibold">Bandwidth</TableHead>
                    <TableHead className="font-semibold">PPA</TableHead>
                    <TableHead className="text-right font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {viewingSchoolGroup?.records?.map((record, idx) => (
                    <TableRow key={record._id} className="hover:bg-slate-50/50">
                      <TableCell className="font-medium text-slate-500">{idx + 1}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-600">{record.school_id || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-700">{record.house_name || '-'}</TableCell>
                      <TableCell className="font-medium text-slate-900">{record.box_no || '-'}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-600">{record.imei_no || '-'}</TableCell>
                      <TableCell>
                        <div className="flex flex-col text-xs">
                          <span className="text-slate-700">Sim: {record.sim_no || '-'}</span>
                          <span className="text-slate-500">Mob: {record.mobile || '-'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">
                          {record.network_percent ? `${record.network_percent}%` : '-'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{record.bandwidth || '-'}</TableCell>
                      <TableCell className="text-xs text-slate-600">{record.ppa_installed_outside || '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (confirm('Delete this single record?')) {
                              handleDeleteRecord(record._id);
                            }
                          }}
                          className="text-slate-400 hover:text-rose-600 hover:bg-rose-50 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// // Comprehensive Business Activities Tab with Edit Permissions
// function BusinessTab() {
//   const { user } = React.useContext(AuthContext);
//   const [activities, setActivities] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [showCreateDialog, setShowCreateDialog] = useState(false);
//   const [editingActivity, setEditingActivity] = useState(null);
//   const [filters, setFilters] = useState({
//     startDate: '',
//     endDate: '',
//     search: ''
//   });
//   const [pageSize, setPageSize] = useState(10);
//   const [pageIndex, setPageIndex] = useState(0);
//   const [showBusinessFilterDialog, setShowBusinessFilterDialog] = useState(false);
//   const [newActivity, setNewActivity] = useState({
//     date: new Date().toISOString().split('T')[0],
//     daily_recharges: '',
//     new_cards: '',
//     lost_cards: '',
//     replaced_cards: '',
//     other_activity: ''
//   });

//   useEffect(() => {
//     fetchActivities();
//   }, [filters]);

//   const fetchActivities = async () => {
//     try {
//       const params = new URLSearchParams();
//       if (filters.startDate) params.append('start_date', filters.startDate);
//       if (filters.endDate) params.append('end_date', filters.endDate);
//       if (filters.search) params.append('search', filters.search);

//       const response = await api.get(`/api/business-activities?${params}`);
//       setActivities(response.data);
//     } catch (error) {
//       toast.error('Failed to load business activities');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleCreateActivity = async () => {
//     try {
//       const activityData = {
//         ...newActivity,
//         daily_recharges: parseFloat(newActivity.daily_recharges) || 0,
//         new_cards: parseInt(newActivity.new_cards) || 0,
//         lost_cards: parseInt(newActivity.lost_cards) || 0,
//         replaced_cards: 0
//       };

//       const response = await api.post(`/api/business-activities`, activityData);

//       if (response.status === 200) {
//         toast.success('Business activity recorded successfully!');
//         setNewActivity({
//           date: new Date().toISOString().split('T')[0],
//           daily_recharges: '',
//           new_cards: '',
//           lost_cards: '',
//           replaced_cards: '',
//           other_activity: ''
//         });
//         setShowCreateDialog(false);
//         fetchActivities();
//       }
//     } catch (error) {
//       toast.error(getErrorMessage(error, 'Failed to create business activity'));
//     }
//   };

//   const handleEditActivity = async () => {
//     if (!editingActivity) return;

//     try {
//       const updateData = {
//         daily_recharges: parseFloat(editingActivity.daily_recharges) || 0,
//         new_cards: parseInt(editingActivity.new_cards) || 0,
//         lost_cards: parseInt(editingActivity.lost_cards) || 0,
//         replaced_cards: 0,
//         other_activity: editingActivity.other_activity
//       };

//       const response = await api.put(`/api/business-activities/${editingActivity.id}`, updateData);

//       if (response.status === 200) {
//         toast.success('Business activity updated successfully!');
//         setEditingActivity(null);
//         fetchActivities();
//       }
//     } catch (error) {
//       toast.error(getErrorMessage(error, 'Failed to update business activity'));
//     }
//   };

//   const canEditActivity = (activity) => {
//     if (!user) return false;
//     if (user.role === 'supervisor_admin') return true;
//     const isOwner = activity.user_id === user.id;
//     const createdAt = activity.created_at || activity.date;
//     return isOwner && isWithinLast24Hours(createdAt);
//   };

//   const hasRemainingEditForUser = (activity) => {
//     if (user?.role === 'supervisor_admin') return true;
//     const count = activity.non_supervisor_edit_count || 0;
//     return count < 1;
//   };

//   const canDeleteActivity = (activity) => {
//     if (!user) return false;
//     if (user.role === 'supervisor_admin') return true;
//     const isOwner = activity.user_id === user.id;
//     const createdAt = activity.created_at || activity.date;
//     return isOwner && isWithinLast24Hours(createdAt);
//   };

//   const handleDeleteActivity = async (activityId) => {
//     try {
//       const response = await api.delete(`/api/business-activities/${activityId}`);
//       if (response.status === 200) {
//         toast.success('Business activity deleted successfully!');
//         fetchActivities();
//       }
//     } catch (error) {
//       toast.error('Failed to delete business activity');
//     }
//   };

//   const clearFilters = () => {
//     setFilters({
//       startDate: '',
//       endDate: '',
//       search: ''
//     });
//   };

//   if (loading) {
//     return (
//       <div className="flex items-center justify-center py-12">
//         <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent"></div>
//       </div>
//     );
//   }

//   return (
//     <div className="space-y-8">
//       {/* Header */}
//       <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
//         <div>
//           <h2 className="text-3xl font-bold text-gray-900 flex items-center">
//             <PhoneCall className="w-8 h-8 mr-3 text-purple-600" />
//             Business Activities
//           </h2>
//           <p className="text-gray-600 mt-2">
//             Track daily recharges, card management, and business operations
//           </p>
//         </div>

//         <div className="flex flex-wrap gap-3">
//           <Button 
//             onClick={() => setShowBusinessFilterDialog(true)}
//             variant="outline"
//             className="border-gray-300"
//           >
//             <Filter className="w-4 h-4 mr-2" />
//             Filter Options
//           </Button>
//           <Button 
//             onClick={() => setShowCreateDialog(true)}
//             className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-lg"
//           >
//             <Plus className="w-4 h-4 mr-2" />
//             Add Activity
//           </Button>
//         </div>
//       </div>

//       {/* Filter Options Dialog */}
//       <Dialog open={showBusinessFilterDialog} onOpenChange={setShowBusinessFilterDialog}>
//         <DialogContent className="sm:max-w-lg">
//           <DialogHeader>
//             <DialogTitle className="text-xl font-bold flex items-center">
//               <Filter className="w-5 h-5 mr-2 text-purple-600" />
//               Filter Options
//             </DialogTitle>
//             <DialogDescription>
//               Filter business activities by date range and search text
//             </DialogDescription>
//           </DialogHeader>
//           <div className="space-y-4">
//             <div>
//               <Label className="text-sm font-medium">Search</Label>
//               <div className="relative mt-1">
//                 <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//                 <Input
//                   placeholder="Search activities..."
//                   value={filters.search}
//                   onChange={(e) => {
//                     setFilters({ ...filters, search: e.target.value });
//                     setPageIndex(0);
//                   }}
//                   className="pl-10"
//                 />
//               </div>
//             </div>
//             <div className="grid grid-cols-2 gap-4">
//               <div>
//                 <Label className="text-sm font-medium">From Date</Label>
//                 <Input
//                   type="date"
//                   value={filters.startDate}
//                   onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
//                   className="mt-1"
//                 />
//               </div>
//               <div>
//                 <Label className="text-sm font-medium">To Date</Label>
//                 <Input
//                   type="date"
//                   value={filters.endDate}
//                   onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
//                   className="mt-1"
//                 />
//               </div>
//             </div>
//           </div>
//           <DialogFooter>
//             <Button 
//               variant="outline" 
//               onClick={() => {
//                 setFilters({ startDate: '', endDate: '', search: '' });
//                 setPageIndex(0);
//               }}
//             >
//               <RefreshCw className="w-4 h-4 mr-2" />
//               Clear Filters
//             </Button>
//             <Button 
//               onClick={() => {
//                 fetchActivities();
//                 setShowBusinessFilterDialog(false);
//               }}
//               className="bg-blue-600 hover:bg-blue-700"
//             >
//               <Search className="w-4 h-4 mr-2" />
//               Apply Filters
//             </Button>
//           </DialogFooter>
//         </DialogContent>
//       </Dialog>

//       {/* Summary Cards */}
//       <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
//         <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
//           <CardContent className="p-6">
//             <div className="flex items-center justify-between">
//               <div>
//                 <p className="text-green-800 font-semibold text-sm">Total Recharges</p>
//                 <p className="text-2xl font-bold text-green-900">
//                   ₹{activities.reduce((sum, a) => sum + a.daily_recharges, 0).toFixed(2)}
//                 </p>
//               </div>
//               <PhoneCall className="w-10 h-10 text-green-600" />
//             </div>
//           </CardContent>
//         </Card>

//         <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
//           <CardContent className="p-6">
//             <div className="flex items-center justify-between">
//               <div>
//                 <p className="text-blue-800 font-semibold text-sm">New Cards</p>
//                 <p className="text-2xl font-bold text-blue-900">
//                   {activities.reduce((sum, a) => sum + a.new_cards, 0)}
//                 </p>
//               </div>
//               <CreditCard className="w-10 h-10 text-blue-600" />
//             </div>
//           </CardContent>
//         </Card>

//         <Card className="bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200">
//           <CardContent className="p-6">
//             <div className="flex items-center justify-between">
//               <div>
//                 <p className="text-orange-800 font-semibold text-sm">Lost/Swap Cards</p>
//                 <p className="text-2xl font-bold text-orange-900">
//                   {activities.reduce((sum, a) => sum + (a.lost_cards || 0) + (a.replaced_cards || 0), 0)}
//                 </p>
//               </div>
//               <AlertTriangle className="w-10 h-10 text-orange-600" />
//             </div>
//           </CardContent>
//         </Card>
//       </div>

//       {/* Activities List */}
//       {(() => {
//         // Client-side filtering based on search
//         const q = filters.search?.trim().toLowerCase() || '';
//         const filteredActivities = q
//           ? activities.filter((a) => {
//               const haystack = `${a.other_activity || ''} ${a.user_name || ''} ${a.date || ''}`.toLowerCase();
//               return haystack.includes(q);
//             })
//           : activities;

//         const totalPages = Math.max(1, Math.ceil(filteredActivities.length / pageSize));
//         const safePageIndex = Math.min(pageIndex, totalPages - 1);
//         const pageStart = safePageIndex * pageSize;
//         const pageActivities = filteredActivities.slice(pageStart, pageStart + pageSize);

//         return (
//       <Card className="shadow-xl border-0 bg-white/95">
//         <CardHeader className="border-b border-gray-100">
//           <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
//             <div>
//               <CardTitle className="text-lg font-bold text-gray-900">Business Activities</CardTitle>
//               <CardDescription>Showing {filteredActivities.length} record(s)</CardDescription>
//             </div>
//             <div className="flex flex-col sm:flex-row sm:items-center gap-3">
//               <Select
//                 value={String(pageSize)}
//                 onValueChange={(value) => {
//                   setPageSize(Number(value));
//                   setPageIndex(0);
//                 }}
//               >
//                 <SelectTrigger className="w-[110px]">
//                   <SelectValue placeholder="Rows" />
//                 </SelectTrigger>
//                 <SelectContent>
//                   <SelectItem value="10">10</SelectItem>
//                   <SelectItem value="25">25</SelectItem>
//                   <SelectItem value="50">50</SelectItem>
//                   <SelectItem value="100">100</SelectItem>
//                 </SelectContent>
//               </Select>
//               <div className="flex items-center gap-2">
//                 <Button
//                   variant="outline"
//                   size="sm"
//                   disabled={safePageIndex <= 0}
//                   onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
//                 >
//                   Prev
//                 </Button>
//                 <span className="text-xs text-gray-500">
//                   {safePageIndex + 1} / {totalPages}
//                 </span>
//                 <Button
//                   variant="outline"
//                   size="sm"
//                   disabled={safePageIndex + 1 >= totalPages}
//                   onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
//                 >
//                   Next
//                 </Button>
//               </div>
//             </div>
//           </div>
//         </CardHeader>
//         <CardContent className="p-0">
//           {filteredActivities.length === 0 ? (
//             <div className="text-center py-16">
//               <PhoneCall className="w-20 h-20 text-gray-200 mx-auto mb-6" />
//               <p className="text-gray-500 text-xl font-semibold mb-2">No Business Activities Found</p>
//               <p className="text-gray-400 text-lg">Start by recording your first business activity</p>
//             </div>
//           ) : (
//             <div className="overflow-x-auto">
//               <Table>
//                 <TableHeader>
//                   <TableRow className="bg-gray-50/80 hover:bg-gray-100/80">
//                     <TableHead className="font-bold text-gray-800 text-left min-w-[120px]">Date</TableHead>
//                     <TableHead className="font-bold text-gray-800 text-right min-w-[140px]">Recharges (₹)</TableHead>
//                     <TableHead className="font-bold text-gray-800 text-center min-w-[100px]">New Cards</TableHead>
//                     <TableHead className="font-bold text-gray-800 text-center min-w-[140px]">Lost/Swap Cards</TableHead>
//                     <TableHead className="font-bold text-gray-800 text-left min-w-[200px]">Other Activity</TableHead>
//                     {user?.role !== 'user' && <TableHead className="font-bold text-gray-800 text-left min-w-[150px]">Submitted By</TableHead>}
//                     <TableHead className="font-bold text-gray-800 text-center min-w-[100px]">Actions</TableHead>
//                   </TableRow>
//                 </TableHeader>
//                 <TableBody>
//                   {pageActivities.map((activity) => (
//                     <TableRow key={activity.id} className="hover:bg-gray-50 border-b border-gray-100">
//                       <TableCell className="py-4">
//                         <div className="flex items-center space-x-2">
//                           <Calendar className="w-4 h-4 text-gray-400" />
//                           <span className="font-medium">{new Date(activity.date).toLocaleDateString()}</span>
//                         </div>
//                       </TableCell>
//                       <TableCell className="text-right">
//                         <div className="flex items-center justify-end space-x-1">
//                           <DollarSign className="w-4 h-4 text-green-600" />
//                           <span className="font-bold text-green-700 text-lg">₹{activity.daily_recharges.toFixed(2)}</span>
//                         </div>
//                       </TableCell>
//                       <TableCell className="text-center">
//                         <Badge className="bg-blue-100 text-blue-800 font-bold">{activity.new_cards}</Badge>
//                       </TableCell>
//                       <TableCell className="text-center">
//                         <Badge className="bg-orange-100 text-orange-800 font-bold">
//                           {(activity.lost_cards || 0) + (activity.replaced_cards || 0)}
//                         </Badge>
//                       </TableCell>
//                       <TableCell className="max-w-xs">
//                         <p className="truncate font-medium text-gray-900" title={activity.other_activity}>
//                           {activity.other_activity || '-'}
//                         </p>
//                       </TableCell>
//                       {user?.role !== 'user' && (
//                         <TableCell>
//                           <div className="flex items-center space-x-2">
//                             <Avatar className="w-8 h-8">
//                               <AvatarFallback className="bg-purple-100 text-purple-700 text-xs">
//                                 {activity.user_name?.split(' ').map(n => n[0]).join('') || 'U'}
//                               </AvatarFallback>
//                             </Avatar>
//                             <div>
//                               <p className="text-sm font-medium text-gray-900">{activity.user_name}</p>
//                               <p className="text-xs text-gray-500">Staff</p>
//                             </div>
//                           </div>
//                         </TableCell>
//                       )}
//                       <TableCell className="text-center">
//                         <DropdownMenu>
//                           <DropdownMenuTrigger>
//                             <MoreHorizontal className="w-4 h-4" />
//                           </DropdownMenuTrigger>
//                           <DropdownMenuContent>
//                             {canEditActivity(activity) && hasRemainingEditForUser(activity) && (
//                               <DropdownMenuItem onClick={() => setEditingActivity(activity)}>
//                                 <Edit className="w-4 h-4 mr-2" />
//                                 Edit
//                               </DropdownMenuItem>
//                             )}
//                             <DropdownMenuItem onClick={() => navigator.clipboard.writeText(`Recharges: ₹${activity.daily_recharges}, New Cards: ${activity.new_cards}, Lost/Swap: ${(activity.lost_cards || 0) + (activity.replaced_cards || 0)}`)}>
//                               <Copy className="w-4 h-4 mr-2" />
//                               Copy Summary
//                             </DropdownMenuItem>
//                             {canDeleteActivity(activity) && (
//                               <DropdownMenuItem 
//                                 onClick={() => {
//                                   if (confirm('Are you sure you want to delete this activity?')) {
//                                     handleDeleteActivity(activity.id);
//                                   }
//                                 }}
//                                 variant="destructive"
//                               >
//                                 <Trash2 className="w-4 h-4 mr-2" />
//                                 Delete
//                               </DropdownMenuItem>
//                             )}
//                           </DropdownMenuContent>
//                         </DropdownMenu>
//                       </TableCell>
//                     </TableRow>
//                   ))}
//                 </TableBody>
//               </Table>
//             </div>
//           )}
//         </CardContent>
//       </Card>
//         );
//       })()}

//       {/* Create Activity Dialog */}
//       <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
//         <DialogContent className="max-w-md">
//           <DialogHeader>
//             <DialogTitle className="flex items-center space-x-2">
//               <PhoneCall className="w-5 h-5 text-purple-600" />
//               <span>Add Business Activity</span>
//             </DialogTitle>
//             <DialogDescription>
//               Record your daily business activities and operations
//             </DialogDescription>
//           </DialogHeader>
//           <div className="space-y-4">
//             <div>
//               <Label htmlFor="activity-date">Date</Label>
//               <Input
//                 id="activity-date"
//                 type="date"
//                 value={newActivity.date}
//                 onChange={(e) => setNewActivity({ ...newActivity, date: e.target.value })}
//                 className="mt-1"
//               />
//             </div>
//             <div>
//               <Label htmlFor="daily-recharges">Daily Recharges (₹)</Label>
//               <div className="relative mt-1">
//                 <DollarSign className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//                 <Input
//                   id="daily-recharges"
//                   type="number"
//                   step="0.01"
//                   placeholder="0.00"
//                   value={newActivity.daily_recharges}
//                   onChange={(e) => setNewActivity({ ...newActivity, daily_recharges: e.target.value })}
//                   className="pl-10"
//                 />
//               </div>
//             </div>
//             <div className="grid grid-cols-2 gap-4">
//               <div>
//                 <Label htmlFor="new-cards">New Cards</Label>
//                 <Input
//                   id="new-cards"
//                   type="number"
//                   placeholder="0"
//                   value={newActivity.new_cards}
//                   onChange={(e) => setNewActivity({ ...newActivity, new_cards: e.target.value })}
//                   className="mt-1"
//                 />
//               </div>
//               <div>
//                 <Label htmlFor="lost-swap-cards">Lost/Swap Cards</Label>
//                 <Input
//                   id="lost-swap-cards"
//                   type="number"
//                   placeholder="0"
//                   value={newActivity.lost_cards}
//                   onChange={(e) => setNewActivity({ ...newActivity, lost_cards: e.target.value, replaced_cards: '' })}
//                   className="mt-1"
//                 />
//               </div>
//             </div>
//             <div>
//               <Label htmlFor="other-activity">Other Activities</Label>
//               <Textarea
//                 id="other-activity"
//                 placeholder="Describe any other business activities..."
//                 value={newActivity.other_activity}
//                 onChange={(e) => setNewActivity({ ...newActivity, other_activity: e.target.value })}
//                 className="mt-1"
//                 rows={3}
//               />
//             </div>
//           </div>
//           <DialogFooter>
//             <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
//               Cancel
//             </Button>
//             <Button onClick={handleCreateActivity} className="bg-purple-600 hover:bg-purple-700">
//               <Plus className="w-4 h-4 mr-2" />
//               Record Activity
//             </Button>
//           </DialogFooter>
//         </DialogContent>
//       </Dialog>

//       {/* Edit Activity Dialog */}
//       <Dialog open={!!editingActivity} onOpenChange={() => setEditingActivity(null)}>
//         <DialogContent className="max-w-md">
//           <DialogHeader>
//             <DialogTitle className="flex items-center space-x-2">
//               <Edit className="w-5 h-5 text-blue-600" />
//               <span>Edit Business Activity</span>
//             </DialogTitle>
//             <DialogDescription>
//               Update business activity details (Owner + Supervisor access only)
//             </DialogDescription>
//           </DialogHeader>
//           {editingActivity && (
//             <div className="space-y-4">
//               <div>
//                 <Label htmlFor="edit-daily-recharges">Daily Recharges (₹)</Label>
//                 <div className="relative mt-1">
//                   <DollarSign className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//                   <Input
//                     id="edit-daily-recharges"
//                     type="number"
//                     step="0.01"
//                     value={editingActivity.daily_recharges}
//                     onChange={(e) => setEditingActivity({ ...editingActivity, daily_recharges: e.target.value })}
//                     className="pl-10"
//                   />
//                 </div>
//               </div>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <Label htmlFor="edit-new-cards">New Cards</Label>
//                   <Input
//                     id="edit-new-cards"
//                     type="number"
//                     value={editingActivity.new_cards}
//                     onChange={(e) => setEditingActivity({ ...editingActivity, new_cards: e.target.value })}
//                     className="mt-1"
//                   />
//                 </div>
//                 <div>
//                   <Label htmlFor="edit-lost-swap-cards">Lost/Swap Cards</Label>
//                   <Input
//                     id="edit-lost-swap-cards"
//                     type="number"
//                     value={String((parseInt(editingActivity.lost_cards) || 0) + (parseInt(editingActivity.replaced_cards) || 0))}
//                     onChange={(e) => setEditingActivity({ ...editingActivity, lost_cards: e.target.value, replaced_cards: 0 })}
//                     className="mt-1"
//                   />
//                 </div>
//               </div>
//               <div>
//                 <Label htmlFor="edit-other-activity">Other Activities</Label>
//                 <Textarea
//                   id="edit-other-activity"
//                   value={editingActivity.other_activity}
//                   onChange={(e) => setEditingActivity({ ...editingActivity, other_activity: e.target.value })}
//                   className="mt-1"
//                   rows={3}
//                 />
//               </div>
//             </div>
//           )}
//           <DialogFooter>
//             <Button variant="outline" onClick={() => setEditingActivity(null)}>
//               Cancel
//             </Button>
//             <Button onClick={handleEditActivity} className="bg-blue-600 hover:bg-blue-700">
//               <Edit className="w-4 h-4 mr-2" />
//               Update Activity
//             </Button>
//           </DialogFooter>
//         </DialogContent>
//       </Dialog>
//     </div>
//   );
// }

// Comprehensive Users Management Tab (Department Removed)
function UsersTab() {
  const { user } = React.useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [roleCounts, setRoleCounts] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'user',
    position: '',
    region: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, [pageIndex, pageSize]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(totalUsers / pageSize));
    setPageIndex((p) => Math.min(p, totalPages - 1));
  }, [totalUsers, pageSize]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', String(pageIndex + 1));
      params.append('page_size', String(pageSize));
      const response = await api.get(`/api/users?${params}`);
      const payload = response.data || {};
      setUsers(payload.items || []);
      setTotalUsers(payload.total || 0);
      setRoleCounts(payload.role_counts || {});
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.email || !newUser.full_name || !newUser.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate region requirement for Staff and Moderator
    if ((newUser.role === 'user' || newUser.role === 'moderator') && !newUser.region) {
      toast.error('Region is required for Staff and Moderator roles');
      return;
    }

    try {
      const response = await api.post(`/api/users`, newUser);

      if (response.status === 200) {
        toast.success('User created successfully!');
        setNewUser({
          username: '',
          email: '',
          full_name: '',
          password: '',
          role: 'user',
          position: '',
          region: ''
        });
        setShowCreateDialog(false);
        fetchUsers();
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You do not have permission to create users');
      } else {
        toast.error(getErrorMessage(error, 'Failed to create user'));
      }
    }
  };

  const handleEditUser = async () => {
    if (!editingUser) return;

    try {
      const updateData = {
        email: editingUser.email,
        full_name: editingUser.full_name,
        role: editingUser.role,
        position: editingUser.position,
        region: editingUser.region
      };

      const response = await api.put(`/api/users/${editingUser.id}`, updateData);

      if (response.status === 200) {
        toast.success('User updated successfully!');
        setEditingUser(null);
        fetchUsers();
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You do not have permission to edit users');
      } else {
        toast.error(getErrorMessage(error, 'Failed to update user'));
      }
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await api.delete(`/api/users/${userId}`);

      if (response.status === 200) {
        toast.success('User deleted successfully!');
        fetchUsers();
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You do not have permission to delete users');
      } else {
        toast.error(getErrorMessage(error, 'Failed to delete user'));
      }
    }
  };

  const handleToggleUserStatus = async (userId, newStatus) => {
    try {
      const response = await api.put(`/api/users/${userId}/status`, { is_active: newStatus });

      if (response.status === 200) {
        toast.success(`User ${newStatus ? 'activated' : 'deactivated'} successfully!`);
        fetchUsers();
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You do not have permission to change user status');
      } else {
        toast.error(getErrorMessage(error, 'Failed to update user status'));
      }
    }
  };

  const handleUserClick = (userId) => {
    navigate(`/users/${userId}`);
  };

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'supervisor_admin': 'Supervisor',
      'admin': 'Admin',
      'moderator': 'Moderator',
      'user': 'Staff'
    };
    return roleMap[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colorMap = {
      'supervisor_admin': 'bg-red-50 text-red-700 border-red-100', // Toned down
      'admin': 'bg-purple-50 text-purple-700 border-purple-100',
      'moderator': 'bg-blue-50 text-blue-700 border-blue-100',
      'user': 'bg-green-50 text-green-700 border-green-100'
    };
    return colorMap[role] || 'bg-slate-50 text-slate-700 border-slate-100';
  };

  const totalPages = Math.max(1, Math.ceil(totalUsers / pageSize));

  // Extract roles explicitly
  const adminCount = (roleCounts?.supervisor_admin || 0) + (roleCounts?.admin || 0);
  const moderatorCount = roleCounts?.moderator || 0;
  const staffCount = roleCounts?.user || 0;

  if (loading && users.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin mb-4 text-blue-500" />
        <p className="text-sm text-slate-500">Loading Team Members...</p>
      </div>
    );
  }

  return (
    <div className="md:p-8 bg-slate-50 min-h-screen font-sans">
      <div className="max-w-[1400px] mx-auto space-y-6">

        {/* Stats Grid - Cleaner Look */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-slate-500 font-medium text-xs uppercase tracking-wider">Admins & Supervisors</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{adminCount}</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-slate-500 font-medium text-xs uppercase tracking-wider">Moderators</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{moderatorCount}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-slate-500 font-medium text-xs uppercase tracking-wider">Staff Members</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{staffCount}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <User className="w-5 h-5 text-green-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Users Table Card */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="p-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-100">
            <div>
              <h1 className="text-xl font-bold text-slate-800">Team Management</h1>
              <p className="text-sm text-slate-500 mt-1">Manage user roles, permissions, and account status</p>
            </div>

            <div className="flex items-center gap-3 self-end flex-wrap">
              {(user?.role === 'supervisor_admin' || user?.role === 'admin') && (
                <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700 h-9 text-xs">
                  <UserPlus className="w-3 h-3 mr-1" />
                  Add Member
                </Button>
              )}

              <div className="relative">
                <select
                  className="appearance-none bg-white border border-slate-200 rounded-lg px-4 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPageIndex(0);
                  }}
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>

              <div className="flex gap-1 items-center">
                <button
                  onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                  disabled={pageIndex <= 0}
                  className={`p-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-400 transition-colors ${pageIndex <= 0 ? 'cursor-not-allowed opacity-50' : 'hover:bg-slate-100 cursor-pointer'}`}
                >
                  <ChevronDown className="rotate-90 w-4 h-4" />
                </button>
                <div className="px-3 py-2 text-sm font-medium text-slate-600">{pageIndex + 1} / {totalPages}</div>
                <button
                  onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={pageIndex + 1 >= totalPages}
                  className={`p-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-400 transition-colors ${pageIndex + 1 >= totalPages ? 'cursor-not-allowed opacity-50' : 'hover:bg-slate-100 cursor-pointer'}`}
                >
                  <ChevronDown className="-rotate-90 w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[800px]">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  <th className="px-5 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">User Profile</th>
                  <th className="px-5 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">Role</th>
                  <th className="px-5 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Contact</th>
                  <th className="px-5 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">Status</th>
                  <th className="px-5 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((userItem) => (
                  <tr
                    key={userItem.id}
                    className="hover:bg-blue-50/20 transition-colors group cursor-pointer"
                    onClick={() => handleUserClick(userItem.id)}
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10 ring-1 ring-slate-200">
                          <AvatarFallback className="bg-blue-50 text-blue-600 font-bold text-xs">
                            {userItem.full_name?.split(' ').map(n => n[0]).join('') || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold text-slate-900 text-sm group-hover:text-blue-600 transition-colors">
                            {userItem.full_name}
                          </p>
                          <p className="text-xs text-slate-500">@{userItem.username}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border ${getRoleBadgeColor(userItem.role)}`}>
                        {getRoleDisplayName(userItem.role)}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center text-sm text-slate-600">
                        <Mail className="w-3.5 h-3.5 mr-2 text-slate-400" />
                        {userItem.email}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-md text-[10px] font-medium ${userItem.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                        {userItem.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <div onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-all">
                              <MoreHorizontal size={18} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => handleUserClick(userItem.id)}>
                              <User className="w-4 h-4 mr-2" />
                              View Profile
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setEditingUser(userItem)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigator.clipboard.writeText(`${userItem.full_name} - ${userItem.email}`)}>
                              <Copy className="w-4 h-4 mr-2" />
                              Copy Info
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleUserStatus(userItem.id, !userItem.is_active)}>
                              {userItem.is_active ? (
                                <>
                                  <Ban className="w-4 h-4 mr-2 text-rose-500" />
                                  <span className="text-rose-600">Deactivate</span>
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
                                  <span className="text-emerald-600">Activate</span>
                                </>
                              )}
                            </DropdownMenuItem>
                            {user?.role === 'supervisor_admin' && userItem.id !== user.id && (
                              <DropdownMenuItem
                                onClick={() => handleDeleteUser(userItem.id, userItem.full_name)}
                                className="text-red-600 focus:text-red-700"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete User
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-8 h-8 text-slate-300" />
                </div>
                <p className="text-slate-500 text-lg font-semibold mb-1">No Team Members Found</p>
                <p className="text-slate-400 text-sm">Get started by adding a new user.</p>
              </div>
            )}
          </div>
        </div>

        {/* Create User Dialog - Cleaned up */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg font-bold">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                </div>
                Add Team Member
              </DialogTitle>
              <DialogDescription>
                Create a new account with specific role access.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="user-fullname" className="text-xs font-semibold text-slate-500">Full Name</Label>
                  <Input
                    id="user-fullname"
                    placeholder="John Doe"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                    className="h-9"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-username" className="text-xs font-semibold text-slate-500">Username</Label>
                  <Input
                    id="user-username"
                    placeholder="johndoe"
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    className="h-9"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="user-email" className="text-xs font-semibold text-slate-500">Email Address</Label>
                <Input
                  id="user-email"
                  type="email"
                  placeholder="john@example.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="h-9"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="user-password" className="text-xs font-semibold text-slate-500">Password</Label>
                <div className="relative">
                  <Input
                    id="user-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="h-9 pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-1/2 transform -translate-y-1/2 h-full px-3 hover:bg-transparent text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="user-role" className="text-xs font-semibold text-slate-500">Role</Label>
                  <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Staff</SelectItem>
                      <SelectItem value="moderator">Moderator</SelectItem>
                      {user?.role === 'supervisor_admin' && (
                        <>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="supervisor_admin">Supervisor</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-region" className="text-xs font-semibold text-slate-500">
                    Region {(newUser.role === 'user' || newUser.role === 'moderator') && <span className="text-red-500">*</span>}
                  </Label>
                  <Select value={newUser.region} onValueChange={(value) => setNewUser({ ...newUser, region: value })}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MH">MH - Maharashtra</SelectItem>
                      <SelectItem value="BH">BH - Bihar</SelectItem>
                      <SelectItem value="WB">WB - West Bengal</SelectItem>
                      <SelectItem value="NE">NE - North East</SelectItem>
                      <SelectItem value="JK">JK - Jharkhand</SelectItem>
                      <SelectItem value="ALL">ALL - All Regions</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="h-9 text-xs">
                Cancel
              </Button>
              <Button onClick={handleCreateUser} className="bg-blue-600 hover:bg-blue-700 h-9 text-xs">
                Create Account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg font-bold">
                <div className="p-2 bg-indigo-50 rounded-lg">
                  <Edit className="w-5 h-5 text-indigo-600" />
                </div>
                Edit User Details
              </DialogTitle>
              <DialogDescription>
                Update profile information and permissions.
              </DialogDescription>
            </DialogHeader>
            {editingUser && (
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-slate-500">Full Name</Label>
                  <Input
                    value={editingUser.full_name}
                    onChange={(e) => setEditingUser({ ...editingUser, full_name: e.target.value })}
                    className="h-9"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-slate-500">Email Address</Label>
                  <Input
                    type="email"
                    value={editingUser.email}
                    onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                    className="h-9"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-slate-500">Role</Label>
                    <Select value={editingUser.role} onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">Staff</SelectItem>
                        <SelectItem value="moderator">Moderator</SelectItem>
                        {user?.role === 'supervisor_admin' && (
                          <>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="supervisor_admin">Supervisor</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-slate-500">Region</Label>
                    <Select value={editingUser.region || ''} onValueChange={(value) => setEditingUser({ ...editingUser, region: value })}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MH">MH - Maharashtra</SelectItem>
                        <SelectItem value="BH">BH - Bihar</SelectItem>
                        <SelectItem value="WB">WB - West Bengal</SelectItem>
                        <SelectItem value="NE">NE - North East</SelectItem>
                        <SelectItem value="JK">JK - Jharkhand</SelectItem>
                        <SelectItem value="ALL">ALL - All Regions</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setEditingUser(null)} className="h-9 text-xs">
                Cancel
              </Button>
              <Button onClick={handleEditUser} className="bg-indigo-600 hover:bg-indigo-700 h-9 text-xs">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// ApprovalsTab removed - status/approval functionality no longer needed

// Complete Locations Tab with GPS Camera Integration
function LocationsTab() {
  const { user } = React.useContext(AuthContext);
  const [locations, setLocations] = useState([]);
  const [totalLocations, setTotalLocations] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [isCapturingLocation, setIsCapturingLocation] = useState(false);
  const defaultsKey = user?.id ? `machineDetailsDefaults_${user.id}` : 'machineDetailsDefaults_guest';
  const [newVisitBandwidthSelect, setNewVisitBandwidthSelect] = useState('');
  const [editVisitBandwidthSelect, setEditVisitBandwidthSelect] = useState('');
  // Pagination and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [filters, setFilters] = useState({ startDate: '', endDate: '' });
  const [showFilterDialog, setShowFilterDialog] = useState(false);

  const getDefaultMachineDetails = () => {
    try {
      const saved = localStorage.getItem(defaultsKey);
      const parsed = saved ? JSON.parse(saved) : {};
      return {
        school_name: parsed.school_name || '',
        school_id: parsed.school_id || '',
        house_name: parsed.house_name || '',
        box_no: parsed.box_no || '',
        imei_no: parsed.imei_no || '',
        sim_no: parsed.sim_no || '',
        mobile: parsed.mobile || '',
        network_percent: parsed.network_percent || '',
        bandwidth: parsed.bandwidth || '',
        ppa_installed_outside: parsed.ppa_installed_outside || ''
      };
    } catch (error) {
      return {
        school_name: '',
        school_id: '',
        house_name: '',
        box_no: '',
        imei_no: '',
        sim_no: '',
        mobile: '',
        network_percent: '',
        bandwidth: '',
        ppa_installed_outside: ''
      };
    }
  };

  const createEmptyVisit = () => ({
    location_name: '',
    description: '',
    visit_purpose: '',
    visit_type: 'Field Visit',
    visit_date: new Date().toISOString().split('T')[0],
    machine_details: getDefaultMachineDetails()
  });

  const [newVisit, setNewVisit] = useState(() => createEmptyVisit());
  const [capturedImages, setCapturedImages] = useState([]);
  const [showCamera, setShowCamera] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchLocations();
  }, [filters.startDate, filters.endDate, pageIndex, pageSize]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(totalLocations / pageSize));
    setPageIndex((p) => Math.min(p, totalPages - 1));
  }, [totalLocations, pageSize]);

  useEffect(() => {
    try {
      setNewVisitBandwidthSelect(getBandwidthSelectValue(getDefaultMachineDetails().bandwidth));
    } catch (error) {
      setNewVisitBandwidthSelect('');
    }
  }, [defaultsKey]);

  useEffect(() => {
    setEditVisitBandwidthSelect(getBandwidthSelectValue(editingLocation?.machine_details?.bandwidth));
  }, [editingLocation?.id]);

  const handleImageClick = (images, startIndex = 0, location) => {
    // Check for actual image data
    let imageData = [];

    if (location?.visit_images && location.visit_images.length > 0) {
      imageData = location.visit_images;
    } else if (images && images.length > 0) {
      imageData = images;
    } else if (capturedImages && capturedImages.length > 0) {
      // Convert file objects to URLs for display
      imageData = capturedImages.map(file => {
        if (file instanceof File) {
          return URL.createObjectURL(file);
        }
        return file;
      });
    }

    if (imageData && imageData.length > 0) {
      setSelectedImages(imageData);
      setCurrentImageIndex(startIndex);
      setShowImageDialog(true);
      toast.success(`Opening ${imageData.length} photo(s)`);
    } else {
      // Create a demo image URL for testing if no images available
      const demoImages = [
        'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkxvY2F0aW9uIFZpc2l0IFBob3RvPC90ZXh0Pjwvc3ZnPg=='
      ];
      setSelectedImages(demoImages);
      setCurrentImageIndex(0);
      setShowImageDialog(true);
      toast.info('Demo photo view (actual photos will appear once uploaded)');
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % selectedImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + selectedImages.length) % selectedImages.length);
  };

  const fetchLocations = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      params.append('page', String(pageIndex + 1));
      params.append('page_size', String(pageSize));
      const response = await api.get(`/api/location/visits?${params}`);
      const payload = response.data || {};
      const normalized = (payload.items || []).map((visit) => {
        const { label: resolvedName, mapsUrl } = getLocationDisplay(visit);
        // Keep testing_cards as string to support alphanumeric IDs like "D85350, B12358"
        const testingCards = visit.testing_cards ?? visit.testingCards ?? null;

        return {
          ...visit,
          testing_cards: testingCards,
          location_name: visit.location_name ?? visit.visit_purpose ?? '',
          description: visit.description ?? visit.notes ?? '',
          machine_details: visit.machine_details ?? null,
          location: visit.location
            ? {
              ...visit.location,
              resolved_name: resolvedName || visit.location?.resolved_name,
              google_maps_url: mapsUrl || visit.location?.google_maps_url,
            }
            : visit.location,
        };
      });
      setLocations(normalized);
      setTotalLocations(payload.total || 0);
    } catch (error) {
      console.error('Failed to load location visits:', error);
      // Graceful fallback - set empty array instead of crashing
      setLocations([]);
      setTotalLocations(0);

      // Show appropriate error message based on error type
      if (error.response?.status === 500) {
        toast.error('Server error loading visits. Please try again later.');
      } else if (error.response?.status === 401) {
        // Auth error handled by interceptor
      } else {
        toast.error('Failed to load location visits');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVisit = async () => {
    if (!newVisit.location_name || !newVisit.description) {
      toast.error('Please fill in City Name and School Name');
      return;
    }
    if (!newVisit.visit_purpose) {
      toast.error('Visit Purpose is required');
      return;
    }
    if (!newVisit.visit_type) {
      toast.error('Visit Type is required');
      return;
    }
    if (!newVisit.testing_cards || String(newVisit.testing_cards).trim() === '') {
      toast.error('Testing Cards is required');
      return;
    }

    // Machine details are OPTIONAL - only include if provided
    const machineDetails = newVisit.machine_details || {};
    const hasMachineDetails = Object.values(machineDetails).some((v) => v && String(v).trim() !== '');
    // No validation error - machine details are optional for location visits


    if (capturedImages.length === 0) {
      toast.error('Please add at least one photo');
      return;
    }

    setIsCapturingLocation(true);
    toast.info('Capturing GPS location... Please wait.');

    try {
      // Get current GPS location - MANDATORY
      let location = {};

      if (!navigator.geolocation) {
        toast.error('Geolocation is not supported by your browser. Cannot save visit.');
        setIsCapturingLocation(false);
        return;
      }

      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            reject,
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0
            }
          );
        });

        location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString(),
          google_maps_url: `https://maps.google.com/maps?q=${position.coords.latitude},${position.coords.longitude}`
        };
      } catch (locationError) {
        console.error('GPS location capture failed:', locationError);
        let errorMsg = 'Failed to get your location. ';
        if (locationError.code === 1) errorMsg += 'Permission denied. Please allow location access.';
        else if (locationError.code === 2) errorMsg += 'Position unavailable. Check your GPS.';
        else if (locationError.code === 3) errorMsg += 'Request timed out.';

        toast.error(errorMsg);
        setIsCapturingLocation(false);
        return; // STOP EXECUTION - Do not save without location
      }

      // Double check validation
      if (!location.latitude || !location.longitude) {
        toast.error('Invalid GPS data. Please try again.');
        setIsCapturingLocation(false);
        return;
      }

      // Create the visit data
      // Validate testing cards (alphanumeric allowed, separated by comma)
      const validateTestingCards = (cards) => {
        if (!cards) return true;
        // Allow spaces, commas, letters, numbers
        const pattern = /^[A-Za-z0-9,\s]+$/;
        return pattern.test(cards);
      };

      if (newVisit.testing_cards && !validateTestingCards(newVisit.testing_cards)) {
        toast.error("Testing cards must be alphanumeric keys separated by commas (e.g. A123, B456)");
        setIsCapturingLocation(false);
        return;
      }

      const machineDetailsPayload = hasMachineDetails ? machineDetails : undefined;

      const visitData = {
        visit_purpose: newVisit.visit_purpose,
        location_name: newVisit.location_name,
        location: location,
        notes: newVisit.description,
        visit_type: newVisit.visit_type,
        visit_date: formatDateForAPI(newVisit.visit_date),
        testing_cards: newVisit.testing_cards || null,  // Send as string to support alphanumeric IDs
        machine_details: machineDetailsPayload
      };

      let response;

      // If we have images, use FormData
      if (capturedImages.length > 0) {
        const formData = new FormData();
        formData.append('visit_purpose', newVisit.visit_purpose);
        formData.append('location', JSON.stringify(location));
        formData.append('location_name', newVisit.location_name);
        formData.append('notes', newVisit.description);
        formData.append('visit_type', newVisit.visit_type || 'Field Visit');
        formData.append('visit_date', formatDateForAPI(newVisit.visit_date));

        // Send testing_cards as string to support alphanumeric card IDs like "D85350, B12358"
        formData.append('testing_cards', newVisit.testing_cards || '');

        if (machineDetailsPayload) {
          formData.append('machine_details', JSON.stringify(machineDetailsPayload));
        }

        // Add captured images
        capturedImages.forEach((image, index) => {
          formData.append('images', image, `visit_photo_${index}.jpg`);
        });

        response = await api.post(`/api/location/visit`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000 // Increased timeout for uploads
        });
      } else {
        // Should not reach here due to validation above, but kept for safety
        throw new Error("Photos are required");
      }

      // Success! Close dialog immediately
      setShowCreateDialog(false);

      // Reset form
      setNewVisit(createEmptyVisit());
      setCapturedImages([]);

      // Show success messages
      toast.success('Location visit recorded successfully!');

      // Refresh locations list
      try {
        await fetchLocations();
      } catch (fetchError) {
        // Ignore refresh errors
      }

    } catch (error) {
      console.error("Save visit error:", error);

      // Extract readable error message from different error structures
      let errorMessage = 'Failed to save visit. Please try again.';

      if (error.response?.data) {
        // Handle different backend error response formats
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          // FastAPI validation error format
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            // Validation errors array
            errorMessage = error.response.data.detail
              .map(err => err.msg || JSON.stringify(err))
              .join(', ');
          } else {
            errorMessage = JSON.stringify(error.response.data.detail);
          }
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsCapturingLocation(false);
    }
  };

  // Image compression helper function
  const compressImage = (file, maxSizeMB = 2, maxWidth = 1920) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const img = new Image();

      reader.onload = (e) => {
        img.src = e.target.result;
      };

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) return reject('Compression failed');

            if (blob.size / 1024 / 1024 > maxSizeMB) {
              reject('Image exceeds size limit after compression');
            } else {
              resolve(new File([blob], file.name, { type: file.type }));
            }
          },
          file.type,
          0.7 // compression quality (70%)
        );
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleImageCapture = async (event) => {
    const files = Array.from(event.target.files);
    const compressedFiles = [];

    for (const file of files) {
      // Check if file is too large (> 3 MB)
      if (file.size / 1024 / 1024 > 3) {
        try {
          const compressed = await compressImage(file, 2);
          compressedFiles.push(compressed);
          toast.success(`Compressed ${file.name} from ${(file.size / 1024 / 1024).toFixed(2)}MB to ${(compressed.size / 1024 / 1024).toFixed(2)}MB`);
        } catch (err) {
          toast.error(`Failed to compress ${file.name}: ${err}`);
        }
      } else {
        compressedFiles.push(file);
      }
    }

    setCapturedImages(prev => [...prev, ...compressedFiles]);
    toast.success(`${compressedFiles.length} image(s) added to visit record`);
  };

  const removeImage = (index) => {
    setCapturedImages(prev => prev.filter((_, i) => i !== index));
    toast.info('Image removed from visit record');
  };

  const canEditLocation = (location) => {
    if (!user) return false;
    if (user.role === 'supervisor_admin') return true;
    const isOwner = location.user_id === user.id;
    const createdAt = location.created_at || location.visit_date;
    return isOwner && isWithinLast24Hours(createdAt);
  };

  const hasRemainingEditForLocation = (location) => {
    if (user?.role === 'supervisor_admin') return true;
    const count = location.non_supervisor_edit_count || 0;
    return count < 1;
  };

  const canDeleteLocation = (location) => {
    if (!user) return false;
    if (user.role === 'supervisor_admin') return true;
    const isOwner = location.user_id === user.id;
    const createdAt = location.created_at || location.visit_date;
    return isOwner && isWithinLast24Hours(createdAt);
  };

  const handleDeleteLocation = async (locationId) => {
    if (!locationId) {
      toast.error('Cannot delete: visit has no valid ID');
      console.error('[Delete] Missing locationId');
      return;
    }
    try {
      console.log('[Delete] Deleting visit id:', locationId);
      const response = await api.delete(`/api/location/visits/${locationId}`);
      if (response.status === 200) {
        toast.success('Location visit deleted successfully!');
        fetchLocations();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to delete location visit';
      console.error('[Delete] Error deleting visit id:', locationId, error.response?.status, detail);
      if (error.response?.status === 404) {
        toast.error('Visit not found. It may have been already deleted.');
        fetchLocations();
      } else {
        toast.error(detail);
      }
    }
  };

  const handleEditLocation = async () => {
    if (!editingLocation) return;

    try {
      const updateData = {
        location_name: editingLocation.location_name,
        description: editingLocation.description,
        visit_date: editingLocation.visit_date,
        testing_cards: editingLocation.testing_cards || null,  // Keep as string for alphanumeric card IDs
        visit_purpose: editingLocation.visit_purpose,
        visit_type: editingLocation.visit_type
      };

      if (!editingLocation.id) {
        toast.error('Cannot update: visit has no valid ID');
        return;
      }
      console.log('[Edit] Updating visit id:', editingLocation.id);
      const response = await api.put(`/api/location/visits/${editingLocation.id}`, updateData);

      if (response.status === 200) {
        toast.success('Location visit updated successfully!');
        setEditingLocation(null);
        fetchLocations();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to update location visit';
      console.error('[Edit] Error updating visit id:', editingLocation?.id, error.response?.status, detail);
      if (error.response?.status === 404) {
        toast.error('Visit not found. It may have been deleted.');
        setEditingLocation(null);
        fetchLocations();
      } else {
        toast.error(detail);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Locations Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
            {user?.role === 'user' ? 'My Location Visits' : 'Team Location Visits'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Track and manage field visits, including photos and location data.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowFilterDialog(true)}
            className="h-9 border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </Button>
          <Button
            onClick={() => {
              const emptyVisit = createEmptyVisit();
              setNewVisit(emptyVisit);
              setNewVisitBandwidthSelect(getBandwidthSelectValue(emptyVisit.machine_details?.bandwidth));
              setCapturedImages([]);
              setShowCreateDialog(true);
            }}
            className="h-9 bg-orange-600 hover:bg-orange-700 text-white shadow-sm"
          >
            <MapPin className="w-4 h-4 mr-2" />
            New Visit
          </Button>
        </div>
      </div>

      {/* Filter Options Dialog for Visits */}
      <Dialog open={showFilterDialog} onOpenChange={setShowFilterDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center text-slate-800">
              <Filter className="w-5 h-5 mr-2 text-orange-600" />
              Filter Visits
            </DialogTitle>
            <DialogDescription>
              Refine your search by location, date, or specific details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Search Keywords</Label>
              <div className="relative mt-1.5">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search city, school, or notes..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPageIndex(0);
                  }}
                  className="pl-9 h-10 border-slate-200 focus:border-orange-300"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">From Date</Label>
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => {
                    setFilters({ ...filters, startDate: e.target.value });
                    setPageIndex(0);
                  }}
                  className="mt-1.5 h-10 border-slate-200"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">To Date</Label>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => {
                    setFilters({ ...filters, endDate: e.target.value });
                    setPageIndex(0);
                  }}
                  className="mt-1.5 h-10 border-slate-200"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => {
                setSearchQuery('');
                setFilters({ startDate: '', endDate: '' });
                setPageIndex(0);
                toast.success('Filters cleared');
              }}
              className="text-slate-500 hover:text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <Button
              onClick={() => setShowFilterDialog(false)}
              className="bg-orange-600 hover:bg-orange-700 text-white shadow-sm"
            >
              Show Results
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visits Table with Pagination */}
      {(() => {
        const q = searchQuery.trim().toLowerCase();
        const visibleLocations = q
          ? locations.filter((loc) => {
            const haystack = `${loc.location_name || ''} ${loc.description || ''} ${loc.visit_purpose || ''} ${loc.notes || ''}`.toLowerCase();
            return haystack.includes(q);
          })
          : locations;

        const totalPages = Math.max(1, Math.ceil(totalLocations / pageSize));
        const safePageIndex = Math.min(pageIndex, totalPages - 1);

        return (
          <Card className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
            <CardHeader className="bg-white border-b border-slate-100 px-6 py-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="bg-orange-50 p-2.5 rounded-lg border border-orange-100">
                    <MapPin className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base font-bold text-slate-800">Visit Log</CardTitle>
                    <CardDescription className="text-xs text-slate-500 mt-0.5">
                      {totalLocations} record{totalLocations !== 1 ? 's' : ''} found
                    </CardDescription>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-500">Rows:</span>
                    <Select
                      value={String(pageSize)}
                      onValueChange={(value) => {
                        setPageSize(Number(value));
                        setPageIndex(0);
                      }}
                    >
                      <SelectTrigger className="w-[70px] h-8 text-xs bg-white border-slate-200">
                        <SelectValue placeholder="Rows" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="h-4 w-px bg-slate-200"></div>

                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0 border-slate-200"
                      disabled={safePageIndex <= 0}
                      onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                    >
                      <ChevronLeft className="w-4 h-4 text-slate-600" />
                    </Button>
                    <span className="text-xs font-medium text-slate-600 min-w-[3rem] text-center">
                      {safePageIndex + 1} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0 border-slate-200"
                      disabled={safePageIndex + 1 >= totalPages}
                      onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
                    >
                      <ChevronRight className="w-4 h-4 text-slate-600" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {visibleLocations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 bg-slate-50/30">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 shadow-sm border border-slate-100">
                    <MapPin className="w-8 h-8 text-slate-300" />
                  </div>
                  <h3 className="text-slate-900 font-semibold text-lg">No Visits Found</h3>
                  <p className="text-slate-500 text-sm mt-1 mb-6 max-w-xs text-center">No location visits match your current filters. Try resetting them.</p>
                  <Button onClick={() => { setSearchQuery(''); setFilters({ startDate: '', endDate: '' }); }} variant="outline" className="border-slate-200 bg-white hover:bg-slate-50">
                    Clear Filters
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 hover:bg-slate-50 border-b border-slate-100">
                        <TableHead className="w-[120px] text-[11px] uppercase font-bold text-slate-500 tracking-wider">Date</TableHead>
                        <TableHead className="min-w-[140px] text-[11px] uppercase font-bold text-slate-500 tracking-wider">City Name</TableHead>
                        <TableHead className="min-w-[180px] text-[11px] uppercase font-bold text-slate-500 tracking-wider">School / Description</TableHead>
                        <TableHead className="text-center text-[11px] uppercase font-bold text-slate-500 tracking-wider">Testing Cards</TableHead>
                        {(user?.role === 'supervisor_admin' || user?.role === 'admin' || user?.role === 'moderator') && (
                          <TableHead className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">By User</TableHead>
                        )}
                        <TableHead className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">GPS Location</TableHead>
                        <TableHead className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">Photos</TableHead>
                        <TableHead className="text-right text-[11px] uppercase font-bold text-slate-500 tracking-wider">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleLocations.map((location) => (
                        <TableRow
                          key={location.id}
                          className="group hover:bg-orange-50/5 transition-colors border-b border-slate-50"
                        >
                          <TableCell className="font-medium text-slate-600 text-xs">
                            {location.visit_date
                              ? new Date(location.visit_date).toLocaleDateString()
                              : location.created_at
                                ? new Date(location.created_at).toLocaleDateString()
                                : '-'}
                          </TableCell>
                          <TableCell className="font-semibold text-slate-800">
                            {location.location_name || location.visit_purpose || '-'}
                            {location.visit_type && (
                              <div className="text-[10px] text-slate-400 font-normal mt-0.5">{location.visit_type}</div>
                            )}
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <div className="truncate text-sm text-slate-600" title={location.description || location.notes}>
                              {location.description || location.notes || '-'}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {location.testing_cards !== null && location.testing_cards !== undefined
                              ? (
                                <Badge variant="outline" className="bg-slate-50 border-slate-200 text-slate-700 font-mono text-[10px]">
                                  {location.testing_cards}
                                </Badge>
                              )
                              : '-'}
                          </TableCell>
                          {(user?.role === 'supervisor_admin' || user?.role === 'admin' || user?.role === 'moderator') && (
                            <TableCell>
                              {location.user_name ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-purple-50 flex items-center justify-center text-purple-700 font-bold text-[10px] ring-1 ring-purple-100">
                                    {location.user_name?.charAt(0).toUpperCase() || 'U'}
                                  </div>
                                  <div className="text-xs font-medium text-slate-700">{location.user_name}</div>
                                </div>
                              ) : '-'}
                            </TableCell>
                          )}
                          <TableCell>
                            {(() => {
                              const { label, mapsUrl } = getLocationDisplay(location);
                              return mapsUrl ? (
                                <a
                                  href={mapsUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-md text-xs transition-colors font-medium"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MapPin className="w-3 h-3" />
                                  <span className="truncate max-w-[100px]">{label || 'View Map'}</span>
                                </a>
                              ) : (
                                <span className="text-slate-400 text-xs italic">No location data</span>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            {location.visit_images?.length > 0 ? (
                              <div
                                className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => handleImageClick(location.visit_images, 0, location)}
                              >
                                <div className="bg-slate-100 p-1.5 rounded-md border border-slate-200">
                                  <Camera className="w-3.5 h-3.5 text-slate-600" />
                                </div>
                                <span className="text-xs font-semibold text-slate-600">{location.visit_images.length}</span>
                              </div>
                            ) : (
                              <span className="text-slate-300 text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-50">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-[180px]">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                {location.location?.google_maps_url && (
                                  <DropdownMenuItem
                                    onClick={() => window.open(location.location.google_maps_url, '_blank')}
                                  >
                                    <MapPin className="w-3.5 h-3.5 mr-2 text-slate-500" />
                                    View Map
                                  </DropdownMenuItem>
                                )}
                                {location.visit_images?.length > 0 && (
                                  <DropdownMenuItem
                                    onClick={() => handleImageClick(location.visit_images, 0, location)}
                                  >
                                    <Camera className="w-3.5 h-3.5 mr-2 text-slate-500" />
                                    View Photos
                                  </DropdownMenuItem>
                                )}

                                {(canEditLocation(location) && hasRemainingEditForLocation(location)) && (
                                  <>
                                    <div className="h-px bg-slate-100 my-1"></div>
                                    <DropdownMenuItem onClick={() => setEditingLocation(location)}>
                                      <Edit className="w-3.5 h-3.5 mr-2 text-blue-500" />
                                      Edit Visit
                                    </DropdownMenuItem>
                                  </>
                                )}

                                {canDeleteLocation(location) && (
                                  <DropdownMenuItem
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleDeleteLocation(location.id)}
                                  >
                                    <Trash2 className="w-3.5 h-3.5 mr-2" />
                                    Delete Visit
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center space-x-2 text-xl font-bold text-slate-800">
              <MapPin className="w-5 h-5 text-orange-600" />
              <span>Add Location Visit</span>
            </DialogTitle>
            <DialogDescription>
              Record a new field visit. Location will be captured automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-4">
            <div className="space-y-2">
              <Label htmlFor="visit-location-name" className="text-xs font-semibold text-slate-500 uppercase">City Name</Label>
              <Input
                id="visit-location-name"
                placeholder="Enter city name"
                value={newVisit.location_name}
                onChange={(e) => setNewVisit({ ...newVisit, location_name: e.target.value })}
                className="h-10 border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="visit-date" className="text-xs font-semibold text-slate-500 uppercase">Visit Date</Label>
              <Input
                id="visit-date"
                type="date"
                value={newVisit.visit_date}
                onChange={(e) => setNewVisit({ ...newVisit, visit_date: e.target.value })}
                className="h-10 border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="visit-school-name" className="text-xs font-semibold text-slate-500 uppercase">School Name</Label>
              <Input
                id="visit-school-name"
                placeholder="Enter school name"
                value={newVisit.description}
                onChange={(e) => setNewVisit({ ...newVisit, description: e.target.value })}
                className="h-10 border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="visit-purpose" className="text-xs font-semibold text-slate-500 uppercase">Visit Purpose</Label>
              <Input
                id="visit-purpose"
                placeholder="Enter visit purpose"
                value={newVisit.visit_purpose}
                onChange={(e) => setNewVisit({ ...newVisit, visit_purpose: e.target.value })}
                className="h-10 border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="visit-type" className="text-xs font-semibold text-slate-500 uppercase">Visit Type</Label>
              <Input
                id="visit-type"
                placeholder="e.g. Field Visit"
                value={newVisit.visit_type}
                onChange={(e) => setNewVisit({ ...newVisit, visit_type: e.target.value })}
                className="h-10 border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="visit-testing-cards" className="text-xs font-semibold text-slate-500 uppercase">Testing Cards</Label>
              <Input
                id="visit-testing-cards"
                type="text"
                placeholder="e.g., A12, TC-09, 5"
                required
                value={newVisit.testing_cards || ''}
                onChange={(e) => setNewVisit({ ...newVisit, testing_cards: e.target.value })}
                className="h-10 border-slate-200"
              />
            </div>
          </div>

          <div className="space-y-3 pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                <Camera className="w-4 h-4 text-slate-500" />
                Photos
              </div>
              <div className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100">
                Required
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center hover:bg-slate-100 transition-colors cursor-pointer relative">
              <Input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                required
                onChange={handleImageCapture}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div className="flex flex-col items-center gap-2">
                <Camera className="w-8 h-8 text-slate-400" />
                <span className="text-sm font-medium text-slate-600">Tap to capture or upload photos</span>
                <span className="text-xs text-slate-400">Supports JPG, PNG</span>
              </div>
            </div>

            {capturedImages.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                {capturedImages.map((file, index) => (
                  <div key={`${file.name}-${index}`} className="relative group bg-white p-2 rounded-lg border border-slate-200 shadow-sm flex items-center justify-between">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="text-xs text-slate-600 truncate">{file.name}</span>
                    </div>
                    <button
                      onClick={() => removeImage(index)}
                      className="text-slate-400 hover:text-red-500 transition-colors p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-slate-200">
              Cancel
            </Button>
            <Button onClick={handleCreateVisit} className="bg-orange-600 hover:bg-orange-700 shadow-sm shadow-orange-100">
              <Save className="w-4 h-4 mr-2" />
              Save Visit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingLocation} onOpenChange={(open) => !open && setEditingLocation(null)}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center space-x-2 text-lg font-bold text-slate-800">
              <Edit className="w-5 h-5 text-orange-600" />
              <span>Edit Location Visit</span>
            </DialogTitle>
            <DialogDescription>
              Update details for this visit record.
            </DialogDescription>
          </DialogHeader>
          {editingLocation && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-visit-location-name" className="text-xs font-semibold text-slate-500 uppercase">City Name</Label>
                <Input
                  id="edit-visit-location-name"
                  value={editingLocation.location_name || ''}
                  onChange={(e) => setEditingLocation({ ...editingLocation, location_name: e.target.value })}
                  className="h-10 border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-visit-date" className="text-xs font-semibold text-slate-500 uppercase">Visit Date</Label>
                <Input
                  id="edit-visit-date"
                  type="date"
                  value={editingLocation.visit_date || ''}
                  onChange={(e) => setEditingLocation({ ...editingLocation, visit_date: e.target.value })}
                  className="h-10 border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-visit-school-name" className="text-xs font-semibold text-slate-500 uppercase">School Name</Label>
                <Input
                  id="edit-visit-school-name"
                  value={editingLocation.description || ''}
                  onChange={(e) => setEditingLocation({ ...editingLocation, description: e.target.value })}
                  className="h-10 border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-visit-purpose" className="text-xs font-semibold text-slate-500 uppercase">Visit Purpose</Label>
                <Input
                  id="edit-visit-purpose"
                  value={editingLocation.visit_purpose || ''}
                  onChange={(e) => setEditingLocation({ ...editingLocation, visit_purpose: e.target.value })}
                  className="h-10 border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-visit-type" className="text-xs font-semibold text-slate-500 uppercase">Visit Type</Label>
                <Input
                  id="edit-visit-type"
                  value={editingLocation.visit_type || ''}
                  onChange={(e) => setEditingLocation({ ...editingLocation, visit_type: e.target.value })}
                  className="h-10 border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-visit-testing-cards" className="text-xs font-semibold text-slate-500 uppercase">Testing Cards</Label>
                <Input
                  id="edit-visit-testing-cards"
                  type="text"
                  value={editingLocation.testing_cards || ''}
                  onChange={(e) => setEditingLocation({ ...editingLocation, testing_cards: e.target.value })}
                  className="h-10 border-slate-200"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingLocation(null)} className="border-slate-200">
              Cancel
            </Button>
            <Button onClick={handleEditLocation} className="bg-orange-600 hover:bg-orange-700 text-white shadow-sm">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent className="sm:max-w-5xl max-h-[95vh] p-0 overflow-hidden bg-black/95 border-0 text-white">
          {/* Custom Close Button */}
          <button
            onClick={() => setShowImageDialog(false)}
            className="absolute right-4 top-4 z-50 p-2 bg-black/50 hover:bg-black/80 rounded-full text-white/70 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="relative w-full h-[85vh] flex items-center justify-center">
            {selectedImages.length > 0 && (
              <img
                src={selectedImages[currentImageIndex]}
                alt={`Visit photo ${currentImageIndex + 1}`}
                className="max-w-full max-h-full object-contain"
              />
            )}

            {/* Navigation buttons */}
            {selectedImages.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-white/10 text-white rounded-full h-12 w-12 border-0"
                  onClick={prevImage}
                >
                  <ChevronLeft className="w-8 h-8" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-white/10 text-white rounded-full h-12 w-12 border-0"
                  onClick={nextImage}
                >
                  <ChevronRight className="w-8 h-8" />
                </Button>
              </>
            )}

            {/* Caption Overlay */}
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent p-6 flex flex-col items-center">
              <div className="flex items-center gap-2 mb-2">
                {selectedImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentImageIndex
                      ? 'bg-white w-4'
                      : 'bg-white/30 hover:bg-white/50'
                      }`}
                  />
                ))}
              </div>
              <p className="text-white/80 text-sm font-medium">
                Photo {currentImageIndex + 1} of {selectedImages.length}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UserProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = React.useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [photoViewerState, setPhotoViewerState] = useState({ open: false, photos: [], activityId: null });
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editProfileData, setEditProfileData] = useState({ full_name: '', email: '', phone: '' });
  const [isCapturingLocation, setIsCapturingLocation] = useState(false);

  const [expensesPageSize, setExpensesPageSize] = useState(10);
  const [expensesPage, setExpensesPage] = useState(0);
  const [activitiesPageSize, setActivitiesPageSize] = useState(10);
  const [activitiesPage, setActivitiesPage] = useState(0);
  const [visitsPageSize, setVisitsPageSize] = useState(10);
  const [visitsPage, setVisitsPage] = useState(0);

  useEffect(() => {
    let mounted = true;
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/users/${userId}/profile`);
        if (!mounted) return;
        setProfileData(response.data);
      } catch (error) {
        if (mounted) setProfileData(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (userId) fetchProfile();
    return () => {
      mounted = false;
    };
  }, [userId]);

  const openPhotos = (photos, activityId) => {
    if (!photos || photos.length === 0) return;
    setPhotoViewerState({ open: true, photos, activityId });
  };

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'supervisor_admin': 'Supervisor',
      'admin': 'Admin',
      'moderator': 'Moderator',
      'user': 'Staff'
    };
    return roleMap[role] || role;
  };

  const canEditProfile = () => {
    return user?.role === 'supervisor_admin' || user?.role === 'admin';
  };

  const downloadCsv = (filename, csvContent) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const toCsvLine = (values) => {
    const escaped = values.map((v) => {
      const s = String(v ?? '');
      const needsQuotes = /[\n\r,\"]/g.test(s);
      const cleaned = s.replace(/\"/g, '""');
      return needsQuotes ? `"${cleaned}"` : cleaned;
    });
    return `${escaped.join(',')}\n`;
  };

  const downloadUserReport = () => {
    const profileUser = profileData?.user;
    if (!profileUser) return;

    const summary = profileData?.summary || {};
    const expenses = Array.isArray(profileData?.expenses) ? profileData.expenses : [];
    const businessActivities = Array.isArray(profileData?.business_activities) ? profileData.business_activities : [];
    const locationVisits = Array.isArray(profileData?.location_visits) ? profileData.location_visits : [];

    let csv = '';
    csv += toCsvLine(['USER REPORT']);
    csv += toCsvLine(['Full Name', profileUser.full_name || '']);
    csv += toCsvLine(['Username', profileUser.username || '']);
    csv += toCsvLine(['Role', getRoleDisplayName(profileUser.role)]);
    csv += toCsvLine(['Region', profileUser.region || '']);
    csv += toCsvLine([]);

    csv += toCsvLine(['SUMMARY']);
    csv += toCsvLine(['Total Expenses', summary.total_expenses ?? 0]);
    csv += toCsvLine(['Total Expense Amount', summary.total_expense_amount ?? 0]);
    // csv += toCsvLine(['Total Business Activities', summary.total_business_activities ?? 0]);
    // csv += toCsvLine(['Total Recharges', summary.total_recharges ?? 0]);
    csv += toCsvLine(['Total Location Visits', summary.total_location_visits ?? 0]);
    csv += toCsvLine([]);

    csv += toCsvLine(['EXPENSES']);
    csv += toCsvLine(['Date', 'Category', 'Description', 'Amount', 'Status']);
    expenses.forEach((e) => {
      csv += toCsvLine([
        e.date || '',
        e.category || '',
        e.description || '',
        e.amount ?? 0,
        e.status || ''
      ]);
    });
    csv += toCsvLine([]);

    // csv += toCsvLine(['BUSINESS ACTIVITIES']);
    // csv += toCsvLine(['Date', 'Daily Recharges', 'New Cards', 'Lost/Swap Cards', 'Other Activity']);
    // businessActivities.forEach((a) => {
    //   csv += toCsvLine([
    //     a.date || '',
    //     a.daily_recharges ?? 0,
    //     a.new_cards ?? 0,
    //     (Number(a.lost_cards || 0) + Number(a.replaced_cards || 0)),
    //     a.other_activity || ''
    //   ]);
    // });
    // csv += toCsvLine([]);

    csv += toCsvLine(['LOCATION VISITS']);
    csv += toCsvLine(['Date', 'Location', 'Description', 'Lat', 'Lng', 'Photos']);
    locationVisits.forEach((v) => {
      const lat = v.location?.latitude;
      const lng = v.location?.longitude;
      csv += toCsvLine([
        v.visit_date || v.created_at || '',
        v.visit_purpose || '',
        v.notes || '',
        lat ?? '',
        lng ?? '',
        (v.visit_images?.length || 0)
      ]);
    });

    const today = new Date().toISOString().split('T')[0];
    const username = profileUser.username || 'user';
    downloadCsv(`user_report_${username}_${today}.csv`, csv);
    toast.success('Report downloaded');
  };

  const openEditProfile = () => {
    if (!canEditProfile()) return;
    const profileUser = profileData?.user;
    if (!profileUser) return;
    setEditProfileData({
      full_name: profileUser.full_name || '',
      email: profileUser.email || '',
      phone: profileUser.phone || ''
    });
    setShowEditDialog(true);
  };

  const saveEditedProfile = async () => {
    if (!canEditProfile()) {
      toast.error('You do not have permission to edit this profile');
      return;
    }
    try {
      await api.put(`/api/users/${userId}`, {
        full_name: editProfileData.full_name,
        email: editProfileData.email,
        phone: editProfileData.phone,
      });
      toast.success('Profile updated successfully');
      setShowEditDialog(false);
      const refreshed = await api.get(`/api/users/${userId}/profile`);
      setProfileData(refreshed.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update profile'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!profileData?.user) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 flex items-center">
              <User className="w-8 h-8 mr-3 text-indigo-600" />
              User Profile
            </h2>
            <p className="text-gray-600 mt-2">Unable to load profile details</p>
          </div>
          <Button variant="outline" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
        <Card className="shadow-xl border-0 bg-white/95">
          <CardContent className="p-10 text-center text-gray-600">Profile not available.</CardContent>
        </Card>
      </div>
    );
  }

  const profileUser = profileData.user;
  const summary = profileData.summary || {};
  const expenses = Array.isArray(profileData.expenses) ? profileData.expenses : [];
  const businessActivities = Array.isArray(profileData.business_activities) ? profileData.business_activities : [];
  const locationVisits = Array.isArray(profileData.location_visits) ? profileData.location_visits : [];

  const sortedExpenses = [...expenses].sort((a, b) => new Date(b.created_at || b.date || 0) - new Date(a.created_at || a.date || 0));
  const sortedActivities = [...businessActivities].sort((a, b) => new Date(b.created_at || b.date || 0) - new Date(a.created_at || a.date || 0));
  const sortedVisits = [...locationVisits].sort((a, b) => new Date(b.created_at || b.visit_date || 0) - new Date(a.created_at || a.visit_date || 0));

  const expensesTotalPages = Math.max(1, Math.ceil(sortedExpenses.length / expensesPageSize));
  const activitiesTotalPages = Math.max(1, Math.ceil(sortedActivities.length / activitiesPageSize));
  const visitsTotalPages = Math.max(1, Math.ceil(sortedVisits.length / visitsPageSize));

  const safeExpensesPage = Math.min(expensesPage, expensesTotalPages - 1);
  const safeActivitiesPage = Math.min(activitiesPage, activitiesTotalPages - 1);
  const safeVisitsPage = Math.min(visitsPage, visitsTotalPages - 1);

  const pagedExpenses = sortedExpenses.slice(safeExpensesPage * expensesPageSize, (safeExpensesPage + 1) * expensesPageSize);
  const pagedActivities = sortedActivities.slice(safeActivitiesPage * activitiesPageSize, (safeActivitiesPage + 1) * activitiesPageSize);
  const pagedVisits = sortedVisits.slice(safeVisitsPage * visitsPageSize, (safeVisitsPage + 1) * visitsPageSize);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50">
      <header className="bg-white/80 backdrop-blur-xl shadow-lg border-b border-white/20 sticky top-0 z-50 mobile-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-4">
              <AllenGroupLogo size="md" />
              <div className="hidden sm:block">
                <h1 className="text-2xl font-black text-gray-900 tracking-tight">ALLENTEK INDIA</h1>
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Business Management System</p>
              </div>
            </div>

            <div className="flex items-center space-x-4 lg:space-x-6">
              <SettingsPanel />
              <div className="flex items-center space-x-3 bg-white/60 backdrop-blur-sm rounded-2xl px-3 py-2 shadow-lg border border-white/30">
                <Avatar className="w-10 h-10 lg:w-12 lg:h-12 ring-2 ring-blue-100 shadow-lg">
                  <AvatarFallback className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white font-bold text-sm lg:text-lg">
                    {user?.full_name?.split(' ').map(n => n[0]).join('') || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden sm:block">
                  <p className="text-sm lg:text-base font-bold text-gray-900">{user?.full_name}</p>
                  <Badge variant="secondary" className="bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 font-semibold text-xs lg:text-sm">
                    {getRoleDisplayName(user?.role)}
                  </Badge>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-xl p-2 lg:p-3 transition-all duration-200"
              >
                <LogOut className="w-4 h-4 lg:w-5 lg:h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mobile-content-padding">
        <div className="space-y-8">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-gray-700 hover:bg-white/70"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>

          <Card className="shadow-xl border-0 bg-white/95">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <Avatar className="w-16 h-16 ring-2 ring-blue-100 shadow-lg">
                    <AvatarFallback className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white font-bold text-xl">
                      {(profileUser.full_name || profileUser.username || 'U')
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">{profileUser.full_name || profileUser.username || 'User Profile'}</h2>
                    <p className="text-gray-600 font-medium">
                      {getRoleDisplayName(profileUser.role)} • @{profileUser.username}
                      {profileUser.region ? ` • ${profileUser.region}` : ''}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  {canEditProfile() && (
                    <Button variant="outline" onClick={openEditProfile} className="bg-white">
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Profile
                    </Button>
                  )}
                  <Button onClick={downloadUserReport} className="bg-blue-600 hover:bg-blue-700">
                    <Download className="w-4 h-4 mr-2" />
                    Download Report
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="shadow-xl border-0 bg-white/95">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-semibold">Expenses</p>
                    <div className="text-3xl font-bold text-green-600">₹{Number(summary.total_expense_amount || 0).toFixed(2)}</div>
                    <div className="text-sm text-gray-500 mt-1">{summary.total_expenses || 0} records submitted</div>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-green-50 border border-green-100 flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-green-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* <Card className="shadow-xl border-0 bg-white/95">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-semibold">Business</p>
                    <div className="text-3xl font-bold text-purple-600">₹{Number(summary.total_recharges || 0).toFixed(2)}</div>
                    <div className="text-sm text-gray-500 mt-1">{summary.total_business_activities || 0} records</div>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center">
                    <PhoneCall className="w-5 h-5 text-purple-700" />
                  </div>
                </div>
              </CardContent>
            </Card> */}

            <Card className="shadow-xl border-0 bg-white/95">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-semibold">Visits</p>
                    <div className="text-3xl font-bold text-orange-600">{summary.total_location_visits || 0}</div>
                    <div className="text-sm text-gray-500 mt-1">Total location visits</div>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-orange-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="shadow-xl border-0 bg-white/95">
            <CardHeader className="border-b border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-xl font-bold text-gray-800">Expense Records</CardTitle>
                  <CardDescription>Most recent first</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Select
                    value={String(expensesPageSize)}
                    onValueChange={(value) => {
                      setExpensesPageSize(Number(value));
                      setExpensesPage(0);
                    }}
                  >
                    <SelectTrigger className="w-[110px]">
                      <SelectValue placeholder="Rows" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={safeExpensesPage <= 0} onClick={() => setExpensesPage((p) => Math.max(0, p - 1))}>
                      Prev
                    </Button>
                    <span className="text-xs text-gray-500">{safeExpensesPage + 1} / {expensesTotalPages}</span>
                    <Button variant="outline" size="sm" disabled={safeExpensesPage + 1 >= expensesTotalPages} onClick={() => setExpensesPage((p) => Math.min(expensesTotalPages - 1, p + 1))}>
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50/80">
                      <TableHead className="font-bold text-gray-800">Date</TableHead>
                      <TableHead className="font-bold text-gray-800">Category</TableHead>
                      <TableHead className="font-bold text-gray-800">Description</TableHead>
                      <TableHead className="font-bold text-gray-800 text-right">Amount</TableHead>
                      <TableHead className="font-bold text-gray-800">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagedExpenses.map((expense) => (
                      <TableRow key={expense.id} className="hover:bg-gray-50">
                        <TableCell>{expense.date ? new Date(expense.date).toLocaleDateString() : '-'}</TableCell>
                        <TableCell className="font-medium">{expense.category || '-'}</TableCell>
                        <TableCell className="max-w-[400px] truncate">{expense.description || '-'}</TableCell>
                        <TableCell className="text-right font-semibold">₹{Number(expense.amount || 0).toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{expense.status || 'pending'}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {pagedExpenses.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-10 text-gray-500">No expenses found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* <Card className="shadow-xl border-0 bg-white/95">
            <CardHeader className="border-b border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-xl font-bold text-gray-800">Business Activity Records</CardTitle>
                  <CardDescription>Lost/Swap cards are merged for display</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Select
                    value={String(activitiesPageSize)}
                    onValueChange={(value) => {
                      setActivitiesPageSize(Number(value));
                      setActivitiesPage(0);
                    }}
                  >
                    <SelectTrigger className="w-[110px]">
                      <SelectValue placeholder="Rows" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={safeActivitiesPage <= 0} onClick={() => setActivitiesPage((p) => Math.max(0, p - 1))}>
                      Prev
                    </Button>
                    <span className="text-xs text-gray-500">{safeActivitiesPage + 1} / {activitiesTotalPages}</span>
                    <Button variant="outline" size="sm" disabled={safeActivitiesPage + 1 >= activitiesTotalPages} onClick={() => setActivitiesPage((p) => Math.min(activitiesTotalPages - 1, p + 1))}>
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50/80">
                      <TableHead className="font-bold text-gray-800">Date</TableHead>
                      <TableHead className="font-bold text-gray-800 text-right">Recharges (₹)</TableHead>
                      <TableHead className="font-bold text-gray-800 text-center">New Cards</TableHead>
                      <TableHead className="font-bold text-gray-800 text-center">Lost/Swap Cards</TableHead>
                      <TableHead className="font-bold text-gray-800">Other Activity</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagedActivities.map((activity) => (
                      <TableRow key={activity.id} className="hover:bg-gray-50">
                        <TableCell>{activity.date ? new Date(activity.date).toLocaleDateString() : '-'}</TableCell>
                        <TableCell className="text-right font-semibold">₹{Number(activity.daily_recharges || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-center">{activity.new_cards || 0}</TableCell>
                        <TableCell className="text-center font-semibold text-orange-600">
                          {Number(activity.lost_cards || 0) + Number(activity.replaced_cards || 0)}
                        </TableCell>
                        <TableCell className="max-w-[400px] truncate">{activity.other_activity || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {pagedActivities.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-10 text-gray-500">No business activities found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card> */}

          <Card className="shadow-xl border-0 bg-white/95">
            <CardHeader className="border-b border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-xl font-bold text-gray-800">Location Visit Records</CardTitle>
                  <CardDescription>Machine details are not shown here</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Select
                    value={String(visitsPageSize)}
                    onValueChange={(value) => {
                      setVisitsPageSize(Number(value));
                      setVisitsPage(0);
                    }}
                  >
                    <SelectTrigger className="w-[110px]">
                      <SelectValue placeholder="Rows" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={safeVisitsPage <= 0} onClick={() => setVisitsPage((p) => Math.max(0, p - 1))}>
                      Prev
                    </Button>
                    <span className="text-xs text-gray-500">{safeVisitsPage + 1} / {visitsTotalPages}</span>
                    <Button variant="outline" size="sm" disabled={safeVisitsPage + 1 >= visitsTotalPages} onClick={() => setVisitsPage((p) => Math.min(visitsTotalPages - 1, p + 1))}>
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50/80">
                      <TableHead className="font-bold text-gray-800">Date</TableHead>
                      <TableHead className="font-bold text-gray-800">Visit Purpose</TableHead>
                      <TableHead className="font-bold text-gray-800">Description</TableHead>
                      <TableHead className="font-bold text-gray-800">Location</TableHead>
                      <TableHead className="font-bold text-gray-800">Photos</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagedVisits.map((visit) => (
                      <TableRow key={visit.id} className="hover:bg-gray-50">
                        <TableCell>
                          {visit.visit_date
                            ? new Date(visit.visit_date).toLocaleDateString()
                            : visit.created_at
                              ? new Date(visit.created_at).toLocaleDateString()
                              : '-'}
                        </TableCell>
                        <TableCell className="font-semibold">{visit.visit_purpose || '-'}</TableCell>
                        <TableCell className="max-w-[400px] truncate">{visit.notes || '-'}</TableCell>
                        <TableCell>
                          {(() => {
                            const { label, mapsUrl } = getLocationDisplay(visit);
                            return mapsUrl ? (
                              <a
                                href={mapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900"
                              >
                                <MapPin className="w-3 h-3" />
                                <span className="text-xs font-medium">{label}</span>
                              </a>
                            ) : (
                              <span className="text-gray-400 text-xs">{label}</span>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={`cursor-pointer transition-colors ${visit.visit_images?.length > 0
                              ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                              : 'bg-gray-100 text-gray-800'
                              }`}
                            onClick={() => visit.visit_images?.length > 0 && openPhotos(visit.visit_images, visit.id)}
                          >
                            <Camera className="w-3 h-3 mr-1" />
                            {visit.visit_images?.length || 0} photos
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {pagedVisits.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-10 text-gray-500">No location visits found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Machine Details Section */}
          <Card className="shadow-xl border-0 bg-white/95">
            <CardHeader className="border-b border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-xl font-bold text-gray-800 flex items-center">
                    <Activity className="w-5 h-5 mr-2 text-teal-600" />
                    Machine Details
                  </CardTitle>
                  <CardDescription>Saved machine configuration records</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {(() => {
                // Load machine details from localStorage for this user
                const recordsKey = profileUser?.id ? `machineDetailsRecords_${profileUser.id}` : null;
                let records = [];
                try {
                  if (recordsKey) {
                    const saved = localStorage.getItem(recordsKey);
                    if (saved) {
                      records = JSON.parse(saved);
                      if (!Array.isArray(records)) records = [];
                    }
                  }
                } catch (e) {
                  records = [];
                }

                if (records.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      No machine details saved for this user
                    </div>
                  );
                }

                return (
                  <div className="space-y-4">
                    {records.slice(0, 5).map((record, idx) => (
                      <div key={record._id || idx} className="p-4 border border-gray-200 rounded-lg bg-gray-50/50">
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm font-semibold text-gray-700">
                            {record._saved_at ? new Date(record._saved_at).toLocaleString() : 'Unknown date'}
                          </div>
                          {record._gps_location && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(record._gps_location.google_maps_url, '_blank')}
                              className="text-blue-700 hover:bg-blue-100"
                            >
                              <MapPin className="w-3 h-3 mr-1" />
                              View GPS
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                          {record.school_name && (
                            <div><span className="text-gray-500">School:</span> <span className="font-medium">{record.school_name}</span></div>
                          )}
                          {record.school_id && (
                            <div><span className="text-gray-500">ID:</span> <span className="font-medium">{record.school_id}</span></div>
                          )}
                          {record.house_name && (
                            <div><span className="text-gray-500">House:</span> <span className="font-medium">{record.house_name}</span></div>
                          )}
                          {record.box_no && (
                            <div><span className="text-gray-500">Box:</span> <span className="font-medium">{record.box_no}</span></div>
                          )}
                          {record.imei_no && (
                            <div><span className="text-gray-500">IMEI:</span> <span className="font-medium">{record.imei_no}</span></div>
                          )}
                          {record.sim_no && (
                            <div><span className="text-gray-500">SIM:</span> <span className="font-medium">{record.sim_no}</span></div>
                          )}
                          {record.mobile && (
                            <div><span className="text-gray-500">Mobile:</span> <span className="font-medium">{record.mobile}</span></div>
                          )}
                          {record.bandwidth && (
                            <div><span className="text-gray-500">Bandwidth:</span> <span className="font-medium">{record.bandwidth}</span></div>
                          )}
                          {record.network_percent && (
                            <div><span className="text-gray-500">Network:</span> <span className="font-medium">{record.network_percent}</span></div>
                          )}
                          {record.ppa_installed_outside && (
                            <div><span className="text-gray-500">PPA Outside:</span> <span className="font-medium">{record.ppa_installed_outside}</span></div>
                          )}
                        </div>
                      </div>
                    ))}
                    {records.length > 5 && (
                      <div className="text-center text-sm text-gray-500">
                        Showing 5 of {records.length} records
                      </div>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold">Edit Profile</DialogTitle>
                <DialogDescription>Update basic profile information.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit_profile_full_name">Full Name</Label>
                  <Input
                    id="edit_profile_full_name"
                    value={editProfileData.full_name}
                    onChange={(e) => setEditProfileData((p) => ({ ...p, full_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_profile_email">Email</Label>
                  <Input
                    id="edit_profile_email"
                    type="email"
                    value={editProfileData.email}
                    onChange={(e) => setEditProfileData((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_profile_phone">Phone</Label>
                  <Input
                    id="edit_profile_phone"
                    value={editProfileData.phone}
                    onChange={(e) => setEditProfileData((p) => ({ ...p, phone: e.target.value }))}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={saveEditedProfile} className="bg-blue-600 hover:bg-blue-700">
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Suspense fallback={null}>
            <PhotoViewerDialog
              open={photoViewerState.open}
              onOpenChange={(open) => setPhotoViewerState((s) => ({ ...s, open }))}
              photos={photoViewerState.photos}
              activityId={photoViewerState.activityId}
            />
          </Suspense>
        </div>
      </main>
    </div>
  );
}

export default App;